import {
  Fragment,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { PlatformBroadcasts } from "../layouts/PlatformBroadcasts";
import { NotificationBell } from "../layouts/NotificationBell";
import { GlobalSearch } from "../layouts/GlobalSearch";
import type { SearchItem } from "./search";
import { TermsGate } from "../../features/terms/TermsGate";
import { DashboardShell, type DashboardNav } from "../layouts/DashboardShell";
import {
  SchoolDashboard,
  SchoolReports,
  ConsentDashboard,
  TeachersScreen,
  ClassesScreen,
  SchoolStudentsScreen,
  SchoolStudentReport,
} from "../../features/dashboards/school";
import type { StudentRow } from "../../shared/api/classes";
import { getAllStudents, getClasses } from "../../shared/api/classes";
import { getSchoolStaff } from "../../shared/api/staff";
import { schoolSearchItems } from "../../features/dashboards/school/searchCorpus";
import { OurPlanScreen } from "../../features/dashboards/school/OurPlanScreen";
import { SchoolGamesScreen } from "../../features/dashboards/school/SchoolGamesScreen";
import { SubscriptionsScreen } from "../../features/dashboards/superadmin/SubscriptionScreens";
import { TeacherApp } from "../../features/dashboards/teacher/TeacherApp";
import {
  AchievementsScreen,
  AnnouncementsScreen,
  AttendanceScreen,
  ChildSwitcher,
  ConsentScreen,
  NotificationsScreen,
  ParentDashboardScreen,
  ProgressDetailScreen,
  SettingsScreen,
} from "../../features/dashboards/parent/ParentScreens";
import { TeacherThread } from "../../features/dashboards/parent/TeacherThread";
import {
  ACCOUNT_WIDE,
  initialParentFlow,
  parentFlow,
  type ParentScreen,
} from "../../features/dashboards/parent/parentFlow";
import { PARENT_COPY } from "../../features/dashboards/parent/copy";
import { BackButton } from "../../shared/components";
import {
  LandingScreen,
  OtpScreen,
  ParentPhoneScreen,
  StaffLoginScreen,
} from "../../features/auth/screens/EntryScreens";
import { InviteClaimScreen } from "../../features/auth/screens/InviteClaimScreen";
import { signInRouteAfterClaim } from "../../features/auth/screens/inviteModel";
import {
  SchoolSignUpClaimScreen,
  SchoolSignUpScreen,
} from "../../features/auth/screens/SchoolSignUp";
import { OperatorLoginScreen } from "../../features/auth/screens/OperatorLoginScreen";
import {
  AccessibilitySettingsScreen,
  CameraSettingsScreen,
} from "../../features/settings/SettingsScreens";
import { WelcomeScreen } from "../../features/marketing/WelcomeScreen";
import { GamePage } from "../../features/marketing/GamePage";
import { ForSchoolsPage } from "../../features/marketing/ForSchoolsPage";
import { peekPendingSection } from "../../features/marketing/useSectionSpy";
import { registerChunk } from "../../shared/prefetch/chunks";

/*
 * 🔴 THE CHILD'S APP IS ITS OWN CHUNK. It carries three.js, R3F, MediaPipe's
 * loader and all five rounds — about a megabyte gzipped — and statically it was
 * in the bundle every sign-in page, teacher and parent downloaded on school wifi.
 * The teacher's play dialog warms it by name (`prefetchChunk("kid-app")`) while
 * the laptop changes hands, so the child does not wait for it either.
 */
const loadKidApp = () => import("../flow/KidApp");
registerChunk("kid-app", loadKidApp);
const KidApp = lazy(() => loadKidApp().then((m) => ({ default: m.KidApp })));
const FreePlayApp = lazy(() =>
  import("../flow/FreePlayApp").then((m) => ({ default: m.FreePlayApp })),
);

/** Under the kid chunk while it arrives — the canvas colour, so the hand-over is not a white flash. */
function KidChunkLoading() {
  return <div className="min-h-dvh bg-canvas" aria-busy="true" />;
}
import {
  AuditLogScreen,
  PlatformChildrenScreen,
  PlatformParentsScreen,
  PlatformCouponsScreen,
  PlatformEngagementScreen,
  PlatformDashboard,
  PlatformOperatorsScreen,
  SchoolsScreen,
  RequestsScreen,
  PlatformBroadcastScreen,
  SystemScreen,
  PLATFORM_COPY,
} from "../../features/dashboards/superadmin";
import {
  endImpersonation,
  startImpersonation,
} from "../../shared/api/platform";
import {
  completeRequiredPasswordChange,
  completeStaffMfa,
  endStaffSession,
  finishForcedMfaEnrolment,
  getTerms,
  isMfaChallenge,
  isMfaEnrolmentChallenge,
  isPasswordChangeChallenge,
  requestParentOtp,
  staffLogin,
  startForcedMfaEnrolment,
  verifyParentOtp,
  type MfaEnrolment,
} from "../../shared/api/auth";
import {
  confirmPhoneCode,
  firebaseConfigured,
  hasPendingCode,
  sendPhoneCode,
  /* The rail decision, pure and tested — see `otpRail.ts`. */
} from "../../shared/auth/firebasePhone";
import { railFor } from "../../features/auth/otpRail";
import { clearOtpHop, loadOtpHop, saveOtpHop } from "../../features/auth/otpResend";
import type { StaffSignIn } from "../../features/auth/signInFlow";
import type { StaffTokens } from "../../shared/stores/staffSession";
import { onSessionRefreshed } from "../../shared/api/refresh";
import { useUnreadMessages } from "../../shared/hooks/useUnreadMessages";
import {
  clearParentSession,
  getParentRefreshToken,
  setParentSession,
  PARENT_SESSION_KEY,
} from "../../shared/stores/parentSession";
import { restoreSession } from "../../shared/stores/restore";
import { decodeJwtClaims } from "../../shared/lib/jwt";
import {
  beginImpersonation,
  clearStaffSession,
  endImpersonationLocally,
  getStaffRefreshToken,
  getStaffToken,
  setStaffSession,
  STAFF_SESSION_KEY,
  type StaffSession,
} from "../../shared/stores/staffSession";
import { useLang, type Lang } from "../../shared/i18n";
import { isOperatorHost } from "../../shared/lib/host";
import {
  MessageIcon,
  NavGroup,
  NavItem,
  PrivacyIcon,
  SignOutIcon,
} from "../../shared/components";
import { IconMore, IconShield } from "../../shared/console/icons";
import {
  DOOR_ROUTE,
  doorAccepts,
  doorFor,
  hashParam,
  homeFor,
  inviteTokenFrom,
  signupTokenFrom,
  isEntryRoute,
  isSettingsRoute,
  mayNavigate,
  navFor,
  pathFor,
  settingsHomeFor,
  titleFor,
  type Door,
  type DoorOutcome,
  type EnrolmentOutcome,
  type RouteId,
} from "./routes";
import {
  NAV_BADGE,
  navIcon,
  navLabel,
  navPermitted,
  navSections,
  navTint,
  pageTitleFor,
  SETTINGS_CONTROL,
  tierLabel,
} from "./nav";
import { badgeCount, useNavBadges } from "../../shared/hooks/useNavBadges";
import { useAccess } from "../../shared/access/useAccess";
import { useRoute } from "./useRoute";
import { consoleScreenFor } from "../../features/console/registry";
import { ConsoleScreenBoundary } from "../../features/console/ConsoleScreenBoundary";
import type { Role } from "../../shared/types/prisma-enums";

/**
 * OD-89 — the thing that makes four units of screens reachable.
 *
 * `App.tsx` rendered *"Unit 1.2 · shells — Components begin in §1.3"* in
 * production while the teacher tier (§8.4), the parent tier (§9.4), the school
 * tier and rollover (§10.1) and the feedback modal (§10.3) sat written, tested,
 * bilingual, calling real endpoints — and mounted nowhere. The production bundle
 * had been 173 modules for four units because the tree-shaker dropped all of it.
 *
 * 🔴 THIS FILE ONLY ROUTES. Every screen below already fetches its own data
 * through `useResource` and already handles its own loading, error and empty
 * states. A router that also fetched would be a second place those decisions
 * live, and the second one is the one that drifts.
 *
 * ✅ SESSION STATE IS NOW RESTORED FROM STORAGE ON BOOT, AND THIS PARAGRAPH USED
 * TO SAY THE OPPOSITE: *"deliberately thin and in memory … persisting the token
 * to save that is the wrong trade on this hardware."* The trade was re-made
 * deliberately and the reasoning lives in `staffSession.ts`, which is the file
 * that pays for it; what matters here is the consequence for routing. A session
 * ends when somebody presses Sign out and at no other moment, so `session`
 * opens as `restoreSession()` rather than as `null`, and a signed-in person who
 * reaches an entry route — the back button, a bookmark — is sent home instead of
 * being shown a door they are already through.
 */

export interface Session {
  role: Role;
  token: string;
  /** X4 — set when this token was minted by `ImpersonationService`. */
  impersonatorId?: string | null;
}

/**
 * P0-10 — "View as school" is READ-ONLY: `ImpersonationReadOnlyGuard` refuses
 * every write made with an impersonation token (403 `IMPERSONATION_READ_ONLY`).
 * The banner is the one place the operator is told before they try.
 */
const IMPERSONATION_BANNER = "Viewing as school (read-only)";

export function AppRouter() {
  const { route, navigate, replace } = useRoute();
  /*
   * 🔴 A NEW SCREEN STARTS AT THE TOP. A hash router swaps the content under a
   * window that keeps its scroll position, so a reader who tapped a game card
   * halfway down the home page landed halfway down the game page — below its
   * hero, mid-paragraph, with no idea where they were. Every route change now
   * starts at the top, the way following a link always has.
   *
   * ⚠️ EXCEPT WHEN A SECTION WAS ASKED FOR. "All games" from a game page goes to
   * `#/` with a pending section (`requestSection`); the home page's spy scrolls
   * there once it mounts, and jumping to the top first would fight it.
   */
  useEffect(() => {
    if (peekPendingSection()) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [route.id]);
  /* One enquiry form: `#/connect` answers with `#/contact-sales` (see its case below). */
  useEffect(() => {
    if (route.id === "connect") replace("contact-sales");
    /*
     * ✅ P1-16 — A BARE `#/invite` IS SENT TO THE TEACHER DOOR, NOT DRAWN AS ONE.
     * It used to render `StaffLoginScreen` with only `onSignIn`, so an account
     * with an authenticator reached a code step whose submit did nothing. There
     * is nothing to claim without a token; the real door has every handler.
     */
    if (route.id === "invite" && inviteTokenFrom(window.location.hash) === null) {
      replace("staff-login");
    }
  }, [route.id, replace]);
  /*
   * ⚠️ THE GAME A CARD ON E1 WAS CLICKED WITH, held here because the card and
   * the log it opens are on two different routes — the same reason
   * `consentClassId` lives above the screen that sets it. `openGames()` with no
   * argument is the sidebar's own way in, and clears any previous seed so the
   * nav item never opens pre-filtered by a card clicked ten minutes ago.
   */
  const [gameFilter, setGameFilter] = useState<string | undefined>(undefined);
  const openGames = useCallback(
    (game?: string) => {
      setGameFilter(game);
      navigate("school-games");
    },
    [navigate],
  );
  /*
   * 🔴 THE LANGUAGE IS THREADED FROM HERE RATHER THAN DEFAULTED PER SCREEN.
   *
   * ⚠️ THIS WAS A REAL BUG ONCE, AND IT IS WORTH KNOWING WHY. Every dashboard
   * screen takes `lang` and defaults it to `'en'`, and this router rendered all
   * of them without it — so the teacher, parent and school tiers were
   * English-only in the running app while their Malayalam dictionaries sat
   * complete and tested beside them. Written, tested, and reachable by nobody
   * who needed it. ADR-044 made English the only value, which settles the bug
   * by removing the other side of it; the threading stays, because a screen
   * reading its language from one place is what a second language would need.
   */
  const { lang } = useLang();

  /*
   * ✅ §10.0 — THE SETTER EXISTS NOW, AND WITH IT THE TIER ABOVE.
   *
   * This file used to read `const [session] = useState(null)` with no way to
   * change it, and said so: *"nothing signs in — StaffLoginScreen predates the
   * login endpoint."* The consequence was larger than a missing form. Every
   * guarded route calls `mayNavigate` with a role, a signed-out browser has
   * none, so the teacher, parent, school AND platform tiers all bounced to
   * sign-in — screens that were written, tested and wired to real endpoints,
   * reachable by nobody. `POST /auth/login` had existed since §10.0.
   *
   * ✅ AND IT OPENS FROM STORAGE RATHER THAN FROM `null`, WHICH IS THE LINE THE
   * WHOLE "it logs me out" REPORT CAME DOWN TO.
   *
   * This read `useState<Session | null>(null)`, and every other piece was
   * already in place: `parentSession.ts` had written the pair to `localStorage`
   * since C1, `refresh.ts` rotated it on a 401, the server honoured a thirty-day
   * refresh token. None of it was ever read back at start-up. So a reload, a
   * bookmark, a re-opened tab and the back button all arrived with `role`
   * undefined, `mayNavigate` refused every guarded route, and the effect below
   * sent a signed-in person to `#/start` — with a good credential sitting on
   * disk the entire time. Persisting a token nothing reads back is
   * indistinguishable from not persisting one.
   *
   * 🔴 A LAZY INITIALISER, NOT AN EFFECT, AND THE DIFFERENCE IS VISIBLE. An
   * effect runs after the first commit, so the first paint would have
   * `session === null` — the redirect below would fire on that render and
   * `replace` the dashboard away before the restore ever landed. React calls
   * this before the first render instead, so there is one render and no flash.
   *
   * ⚠️ REACT STATE **AND** THE MODULE STORE, WHICH IS A DUPLICATE ON PURPOSE.
   * The store is what `shared/api/` reads (plain `fetch` helpers are outside
   * React's lifecycle); this copy is what makes the router re-render when the
   * session changes. `signIn` writes both from one source in one place, and
   * `restoreSession` reads the store the same way, so they cannot disagree.
   */
  const [session, setSession] = useState<Session | null>(restoreSession);

  /*
   * 🔴 CHANGE 19 §6 — WHETHER THIS SESSION IS BEHIND THE TERMS GATE.
   *
   * `null` is "not asked yet" and is deliberately not `false`: rendering the
   * dashboard for the moment between sign-in and the answer would flash the
   * screen §6 exists to keep shut, and every request it fired would come back
   * 403 anyway.
   *
   * ⚠️ THE ANSWER COMES FROM THE SERVER AND IS NOT RECOMPUTED HERE. `required`
   * folds the role in — it is false for every role `TermsGuard` does not gate —
   * so a client deriving it from `acceptedAt === null` would put a blocking
   * screen in front of a teacher whose head signs on their behalf.
   */
  const [termsPending, setTermsPending] = useState(false);

  /*
   * ✅ THE OTHER HALF OF THE DUPLICATE ABOVE, KEPT HONEST BY SUBSCRIPTION.
   *
   * `client.ts` rotates an expired access token on a 401 and writes the result
   * to the module store. This copy is what every screen actually receives (see
   * `auth` below), so without this it would keep handing out the token that just
   * expired — each call would 401, rotate, and succeed on the replay, forever,
   * at twice the requests. `signIn` writes both from one place; this is the same
   * rule for the writes `signIn` is not present for.
   *
   * ⚠️ A `null` TOKEN MEANS THE ROTATION WAS REFUSED, WHICH IS A SIGN-OUT. The
   * store has already been cleared by then; dropping the session here is what
   * makes the router send them back to a door instead of rendering a dashboard
   * whose every request 401s.
   */
  useEffect(
    () =>
      onSessionRefreshed((event) => {
        setSession((current) => {
          if (!current) return current;
          // Two tiers, one router. Only the signed-in one's rotation applies.
          const isParent = current.role === "PARENT";
          if (isParent !== (event.tier === "parent")) return current;
          if (event.accessToken === null) return null;
          return { ...current, token: event.accessToken };
        });
      }),
    [],
  );

  /*
   * 🔴 SIGN OUT IN ONE TAB MEANS SIGNED OUT IN ALL OF THEM, AND WITHOUT THIS THE
   * PERSISTENCE ABOVE WOULD HAVE MADE THAT LESS TRUE THAN IT WAS.
   *
   * A session that survives a reload is a session two tabs can share, and the
   * `storage` event is the only notification a browser gives that another one
   * changed it. Two things arrive through it:
   *
   *   · **A sign-out.** The other tab has already had `/auth/logout` revoke the
   *     refresh token, so this tab is holding a credential that is dead. Left
   *     alone it would not even fail usefully: the next request 401s, the
   *     rotation finds no token to present and declines, and the screen settles
   *     into an error state with the session still "signed in" — a dashboard
   *     nobody can use and nobody is sent away from. On the shared laptop this
   *     product runs on, it is also somebody else's dashboard, still open.
   *   · **A rotation.** The other tab's replacement pair is now the live one.
   *     Adopting it here saves this tab a 401 it would otherwise have to take to
   *     find out; `staffSession.ts`'s `sync` is what makes the store agree.
   *
   * ⚠️ A SIGNED-OUT TAB IS NEVER SIGNED **IN** BY THIS. Following a sign-in that
   * happened elsewhere would yank somebody mid-sign-in on this tab into an
   * account they were not typing a password for. The event only ever narrows
   * what this tab holds, never widens it; a tab that is behind picks the session
   * up on its next load, which is what `restoreSession` is for.
   *
   * ⚠️ AN IMPERSONATION SESSION IS UNAFFECTED, and that falls out rather than
   * being special-cased: X4 is memory-only, so `restoreSession` returns the
   * session already in hand and the comparison below finds nothing to change.
   */
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      // `null` is `localStorage.clear()`, which is every key at once.
      if (
        event.key !== null &&
        event.key !== STAFF_SESSION_KEY &&
        event.key !== PARENT_SESSION_KEY
      ) {
        return;
      }
      /*
       * ⚠️ READ OUTSIDE THE UPDATER, WHICH HAS TO STAY PURE. `restoreSession`
       * brings the module store level with storage — a write, not a read — and
       * React invokes an updater twice under `StrictMode`. It is idempotent, so
       * nothing would break today; a store read that is only accidentally safe
       * to run twice is not a thing to leave inside a function React is entitled
       * to call as often as it likes.
       */
      const next = restoreSession();
      setSession((current) => {
        if (current === null || next === null) return null;
        if (next.role === current.role && next.token === current.token)
          return current;
        return next;
      });
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  /**
   * ✅ THE SECOND FACTOR'S HALF-WAY STATE, AND IT IS DELIBERATELY NOT A SESSION.
   *
   * 🔴 IT HOLDS A `mfaToken`, NEVER THE PASSWORD. The obvious implementation
   * stashes the email and password and re-submits both with the code — which
   * keeps a plaintext credential in a React state variable for as long as it
   * takes somebody to unlock a phone, open an app and read six digits, and puts
   * it in every heap dump and every React DevTools inspection in between. The
   * server hands back a five-minute assertion that grants nothing on its own,
   * precisely so this does not have to.
   *
   * ⚠️ `door` IS WHICH SIGN-IN SCREEN ASKED. Every door has to sign a wrong-tier
   * account straight back out afterwards (see `finishSignInAtDoor`), and that
   * decision happens *after* the code is accepted rather than before — so the
   * challenge has to remember which of the three flows it belongs to.
   */
  const [pendingMfa, setPendingMfa] = useState<{
    mfaToken: string;
    door: Door;
    /**
     * P0-13 — the "Keep me signed in" box as it was on the password step. The
     * code step does not ask again, so the challenge has to carry the answer.
     */
    remember: boolean | undefined;
  } | null>(null);

  /**
   * 🔴 THE ONE MOMENT A SIGNED-IN PERSON IS MEANT TO BE ON A DOOR ROUTE, AND
   * THEREFORE THE ONE EXEMPTION FROM THE ENTRY-ROUTE BOUNCE BELOW.
   *
   * `finishEnrolment` adopts the session and deliberately does **not** navigate,
   * because the response carries the only copy of the recovery codes that will
   * ever exist and the operator has to read them first. That leaves a live
   * session sitting on `#/operator` — which is exactly the state
   * `isEntryRoute` was added to redirect away from. Without this flag the fix
   * for the back button would silently destroy the recovery codes: the screen
   * would be replaced by the dashboard the instant they were minted.
   *
   * ⚠️ IT IS CLEARED BY `enterAfterEnrolment`, which is the operator SAYING they
   * have saved them, and by `signOut`. Not by a timer and not by the route
   * changing — the codes screen owns its own dismissal.
   */
  const [awaitingRecoveryCodes, setAwaitingRecoveryCodes] = useState(false);

  /**
   * ✅ THE COMPULSORY FIRST ENROLMENT, HELD THE SAME WAY AND FOR THE SAME REASON.
   *
   * 🔴 A SECOND FACTOR IS COMPULSORY FOR OPERATORS, SO THE SIGN-IN SCREEN HAS TO
   * BE ABLE TO CREATE ONE. The alternative — refuse the sign-in and tell them to
   * enrol — is a locked door with no handle: the only screen that could enrol
   * them is behind the sign-in that just refused. This holds a fifteen-minute
   * token worth exactly what the password already proved.
   */
  /**
   * 🔴 THE SAME TOKEN IN A REF, AND WITHOUT IT THE ENROLMENT COULD NOT START.
   *
   * `signInAsOperator` calls `setPendingEnrolment(...)` and returns `'enrol'` in
   * the same tick, and the screen calls `onStartEnrolment()` the moment it sees
   * that — before React has committed the state or re-rendered, so the callback
   * still closed over `pendingEnrolment === null`. It threw "No sign-in in
   * progress", the screen's inner catch maps every failure to one state, and a
   * compulsory enrolment presented itself to the operator as **"Couldn't reach
   * Kalqy. Check the connection and try again."** — a network error, on a
   * request that was never made.
   *
   * ⚠️ A REF RATHER THAN THREADING THE TOKEN THROUGH THE RETURN VALUE, because
   * the property worth keeping is that the SCREEN NEVER HOLDS THE TOKEN. Handing
   * it back so it can pass it to the next call would put a fifteen-minute
   * credential in component state for as long as somebody takes to install an
   * authenticator — which is the exact shape the code path already refuses for
   * the password.
   *
   * ⚠️ THE STATE STAYS. It is what `mfaPending`-style rendering and any future
   * "an enrolment is in progress" chrome would read; a ref alone is invisible to
   * React. Both are written together, in one place, so they cannot disagree.
   */
  const enrolmentTokenRef = useRef<string | null>(null);

  /**
   * 🔴 AND THE DOOR IT BEGAN AT, FOR THE SAME REASON AND WITH THE SAME LIFETIME.
   *
   * A compulsory enrolment is the one path that reaches a session WITHOUT going
   * through `signInAt`'s tail: `finishEnrolment` gets the token pair straight
   * from `/auth/mfa/enrol/finish`. Without this, an operator enrolling at the
   * teacher door would be adopted with no wrong-door check at all — which is the
   * original bug wearing a different hat, and the one an operator hits exactly
   * once, on the sign-in where they have no second factor yet.
   */
  const enrolmentDoorRef = useRef<Door | null>(null);

  /**
   * ✅ P3-16 — THE FORCED CHANGE'S TOKEN, HELD EXACTLY AS THE ENROLMENT TOKEN IS.
   *
   * `User.mustChangePassword` (someone else chose this password) answers the
   * sign-in with a purpose-scoped change token and no session. The screen gets
   * two text boxes and `changePassword`; the token, the door it began at and the
   * "Keep me signed in" answer stay here — the property `enrolmentTokenRef`
   * exists to keep is that the SCREEN NEVER HOLDS A CREDENTIAL.
   */
  const changeRef = useRef<{
    token: string;
    door: Door;
    remember: boolean | undefined;
  } | null>(null);

  /**
   * The half of a sign-in that runs once tokens genuinely exist.
   *
   * 🔴 EXTRACTED SO THE PASSWORD PATH AND THE CODE PATH CANNOT DIVERGE. Both end
   * here, so `setStaffSession`, the fail-closed role check and the `replace` are
   * written once — the alternative is two copies, and the one that gets a fix is
   * whichever the author was looking at.
   */
  const adoptSession = useCallback(
    (tokens: StaffTokens, remember: boolean | undefined): StaffSession => {
    /*
     * ⚠️ P0-13 — WHERE THE PAIR LIVES. Unticked → this tab only
     * (`sessionStorage`); ticked, or a door with no box (the operator's, whose
     * session the server caps at 12 hours) → this browser. `staffSession.ts`'s
     * `tierFor` is the rule; this only says which answer was given.
     */
    const next: StaffSession | null = setStaffSession(tokens, {
      remember: remember ?? true,
    });
    if (!next)
      throw Object.assign(new Error("Unusable session token"), { status: 401 });
    setSession({
      role: next.role,
      token: next.accessToken,
      impersonatorId: next.impersonatorId,
    });
    return next;
    },
    [],
  );

  /*
   * ✅ ONE DOOR PER TIER, AND THIS IS THE FUNCTION THAT MAKES A DOOR MEAN
   * ANYTHING. It was `finishOperatorSignIn` and applied to exactly one of the
   * three, which is why an operator's credentials and authenticator code worked
   * perfectly well at the school admin's address.
   *
   * The network half is identical at all three: one `POST /auth/login`, no role
   * asserted by the client, the account decides. What differs is what happens
   * when the account turns out to belong to another tier, and that decision
   * cannot live in the screen — it has to *undo a session this function just
   * created*, which is the router's job and nothing else's.
   *
   * 🔴 A WRONG-DOOR ACCOUNT IS SIGNED STRAIGHT BACK OUT, LOCALLY AND REMOTELY.
   * Letting a teacher's session start on the operator hostname would make the
   * separation a decoration: the sidebar would render the teacher tier at
   * `operator.kalqy.app`, and a support call about "the operator site" would be
   * about a screen that is not the operator site. The same now holds in every
   * direction — an operator who signs in at `#/school/signin` is not left
   * holding a platform session that a school admin's bookmark created.
   *
   * ⚠️ IT IS NOT AN AUTHORISATION BOUNDARY AND MUST NOT BE MISTAKEN FOR ONE.
   * What stops a teacher opening the platform tier is `@Roles('SUPER_ADMIN')`
   * on the server and `mayNavigate` in the browser, both unchanged and both
   * still the only things load-bearing. This decides which ADDRESS an account
   * may complete a sign-in at, which is a product separation.
   */
  /**
   * The tail shared by every door's two entry points — password-only, and
   * password-then-code.
   *
   * 🔴 THE "WRONG DOOR" CHECK RUNS **AFTER** THE SECOND FACTOR, NOT BEFORE IT.
   * Refusing at the challenge step would tell an unauthenticated caller which
   * addresses are operator accounts, before any code was typed — and the whole
   * claim of these screens is that they assert nothing the server has to
   * disprove. So somebody who wanders in completes the entire sign-in and is
   * then signed straight back out and pointed at their own door.
   *
   * ⚠️ WHICH MEANS THE ANSWER LEAKS NOTHING. Reading it requires having already
   * authenticated as that very account — password and second factor both — so
   * the only person who can see it is the one who already holds the credential
   * it describes.
   */
  const finishSignInAtDoor = useCallback(
    (tokens: StaffTokens, door: Door, remember: boolean | undefined): DoorOutcome => {
      const next = adoptSession(tokens, remember);

      if (!doorAccepts(door, next.role)) {
        /*
         * ⚠️ `adoptSession` HAS ALREADY SET THE SESSION, so this undoes it —
         * locally first and remotely unawaited, `signOut`'s rule, because a
         * sign-out that needs the wifi is not one.
         *
         * 🔴 THE REMOTE CALL MATTERS AS MUCH AS THE LOCAL ONE. Clearing only the
         * browser would leave a live refresh session on the server for an
         * account that was told it had not signed in — a session nobody holds
         * and nobody will end.
         */
        const refresh = getStaffRefreshToken();
        clearStaffSession();
        setSession(null);
        if (refresh) void endStaffSession(refresh);
        /*
         * Where they SHOULD have gone, so the screen can offer it as a link
         * rather than making somebody guess a second address. `null` for a role
         * with no staff door at all, which the server has already refused at
         * `/auth/login` — belt, and it costs one branch.
         */
        return { at: "wrong-door", door: doorFor(next.role) };
      }

      // `replace`, not `navigate` — back must not return to a form whose submit
      // is now pointless, which reads as having been signed out.
      replace(homeFor(next.role));
      return { at: "signed-in" };
    },
    [replace, adoptSession],
  );

  /**
   * 🔴 ONE IMPLEMENTATION TAKING THE DOOR, RATHER THAN THREE CALLBACKS. The
   * three doors differ in exactly one value, and three copies of this function
   * would be three places for the enrolment ref, the challenge branch and the
   * wrong-door tail to drift apart — with the copy that gets a fix being
   * whichever one the author was looking at.
   *
   * ⚠️ IT RESOLVES `'mfa'` AND `'enrol'` WITHOUT NAVIGATING. A `Promise<void>`
   * would leave the sign-in button spinning forever for every enrolled account,
   * because resolving used to mean "the router has already replaced this screen".
   */
  /**
   * What the server said after a correct password (or a completed forced
   * change, which continues the same sign-in) — one implementation, so the
   * password path and the change path cannot handle a challenge differently.
   */
  const continueSignIn = useCallback(
    (
      result: Awaited<ReturnType<typeof staffLogin>>,
      door: Door,
      remember: boolean | undefined,
    ): StaffSignIn => {
      /*
       * ✅ P3-16 — "correct password, and somebody else chose it". No session
       * yet: the change token goes in a ref, the screen opens the change step.
       */
      if (isPasswordChangeChallenge(result)) {
        changeRef.current = { token: result.changeToken, door, remember };
        return { at: "change-password" };
      }

      /*
       * 🔴 A CHALLENGE IS NOT A SIGN-IN AND MUST NOT REACH `setStaffSession`.
       * The union is discriminated exactly so this branch is a compile-time
       * obligation rather than a thing somebody remembers.
       *
       * ⚠️ THE DOOR IS REMEMBERED HERE AND SPENT IN `submitMfaCode`, because the
       * wrong-door decision happens after the code is accepted. A challenge that
       * forgot which door it began at would complete at whichever door the
       * previous sign-in used.
       */
      if (isMfaChallenge(result)) {
        setPendingMfa({ mfaToken: result.mfaToken, door, remember });
        return { at: "mfa" };
      }

      /*
       * 🔴 REACHABLE FROM EVERY DOOR, AND IT HAS TO BE. A second factor is
       * compulsory for operators, so the door an operator actually arrives at
       * must be able to create the first one — otherwise the enrolment challenge
       * comes back to a screen that renders nothing for it and a button that
       * spins forever.
       *
       * ⚠️ THE WRONG-DOOR CHECK STILL RUNS, one step later: enrolment finishes
       * through `finishEnrolment`, which adopts the session, and an operator who
       * enrolled at the teacher door is refused there rather than here.
       */
      if (isMfaEnrolmentChallenge(result)) {
        /* 🔴 THE REF FIRST AND SYNCHRONOUSLY. The screen calls `onStartEnrolment`
           in the same tick as this returns, before React has committed the
           state — see `enrolmentTokenRef`. */
        enrolmentTokenRef.current = result.enrolmentToken;
        enrolmentDoorRef.current = door;
        return { at: "enrol" };
      }

      /*
       * 🔴 A TOKEN WHOSE ROLE CLAIM WE DO NOT RECOGNISE IS NOT A SIGN-IN.
       * `sessionFromTokens` fails closed — the same rule `homeFor` applies to an
       * unrecognised role, one layer earlier. `adoptSession` throws, which puts
       * the screen back into its refused state rather than leaving somebody
       * signed in with an empty menu and no way to understand why.
       */
      return finishSignInAtDoor(result, door, remember);
    },
    [finishSignInAtDoor],
  );

  const signInAt = useCallback(
    async (
      door: Door,
      email: string,
      password: string,
      /** P0-13 — `undefined` at the operator door, which has no box. */
      remember: boolean | undefined,
    ): Promise<StaffSignIn> => {
      /*
       * 🔴 THE DOOR GOES TO THE SERVER NOW, AND THAT MOVED THE REFUSAL EARLIER.
       * It used to be spent only here, after both factors — so an operator who
       * typed a password at the teacher door was asked for an authenticator code
       * and only then turned away, which read as the sign-in half-working. The
       * server now refuses the mismatch at the password step with the same
       * generic 401 as a wrong password.
       *
       * ⚠️ THE CHECKS BELOW STAY, ALL THREE OF THEM. This one is a product
       * behaviour and the server is free to ignore an absent or unknown `door`;
       * `finishSignInAtDoor` and `finishEnrolment` are what actually guarantee a
       * session never starts on the wrong tier, and removing them would make the
       * separation depend on the server having honoured a hint.
       */
      const result = await staffLogin(email, password, door, remember);
      return continueSignIn(result, door, remember);
    },
    [continueSignIn],
  );

  const signIn = useCallback(
    (email: string, password: string, remember: boolean) =>
      signInAt("teacher", email, password, remember),
    [signInAt],
  );
  const signInAsSchoolAdmin = useCallback(
    (email: string, password: string, remember: boolean) =>
      signInAt("school", email, password, remember),
    [signInAt],
  );
  /* No box at the operator door (D9): the server caps the session at 12 hours. */
  const signInAsOperator = useCallback(
    (email: string, password: string) =>
      signInAt("operator", email, password, undefined),
    [signInAt],
  );

  /**
   * ✅ P3-16 — the forced change, then the rest of the same sign-in.
   *
   * ⚠️ THE TOKEN IS CLEARED BEFORE THE REQUEST, NOT AFTER. It is single-use on
   * the server; a second submit holding it would only earn a 401 that reads as
   * "this took too long". A failure sends the person back to the password form,
   * which issues a fresh one.
   */
  const changePassword = useCallback(
    async (newPassword: string): Promise<StaffSignIn> => {
      const pending = changeRef.current;
      if (!pending)
        throw Object.assign(new Error("No sign-in in progress"), {
          status: 401,
        });
      const result = await completeRequiredPasswordChange(
        pending.token,
        newPassword,
        pending.remember,
      );
      changeRef.current = null;
      return continueSignIn(result, pending.door, pending.remember);
    },
    [continueSignIn],
  );

  /**
   * ✅ P2-02 — the session `POST /auth/invite/claim` hands back when asked.
   *
   * 🔴 SESSION-ONLY, ALWAYS. The person who just set a password never saw a
   * "Keep me signed in" box, so the safe answer is the one D9 defaults to: this
   * tab, 12 hours (the server issues it so). A fail-closed role check, as every
   * other sign-in path makes, then straight into their tier — a new school admin
   * lands on the console's Pay step instead of typing the password again.
   */
  const adoptClaimSession = useCallback(
    (tokens: StaffTokens): void => {
      const next = adoptSession(tokens, false);
      replace(homeFor(next.role));
    },
    [adoptSession, replace],
  );

  /**
   * Step one of a forced enrolment: mint the secret and hand back the QR.
   *
   * ⚠️ THE SCREEN NEVER SEES THE ENROLMENT TOKEN, exactly as it never sees the
   * challenge token or the password. It gets a QR to draw and two callbacks.
   */
  const startEnrolment = useCallback(async (): Promise<MfaEnrolment> => {
    const token = enrolmentTokenRef.current;
    if (!token)
      throw Object.assign(new Error("No sign-in in progress"), { status: 401 });
    return startForcedMfaEnrolment(token);
  }, []);

  /**
   * Step two: the code turns it on, and the response carries the session — both
   * factors have now been presented in one flow.
   *
   * 🔴 THE RECOVERY CODES COME BACK AND THE SCREEN MUST SHOW THEM BEFORE ANYONE
   * NAVIGATES. So this adopts the session but deliberately does **not**
   * `replace` — `confirmEnrolment` returns the codes and the caller decides when
   * the operator has finished reading them. Navigating here would replace the
   * screen holding the only copy that will ever exist.
   */
  const finishEnrolment = useCallback(
    async (code: string): Promise<EnrolmentOutcome> => {
      const token = enrolmentTokenRef.current;
      const door = enrolmentDoorRef.current;
      if (!token || !door) {
        throw Object.assign(new Error("No sign-in in progress"), {
          status: 401,
        });
      }

      const result = await finishForcedMfaEnrolment(token, code);
      /* Enrolment is the operator's path (no box, 12-hour server cap). */
      const next = adoptSession(result, undefined);
      enrolmentTokenRef.current = null;
      enrolmentDoorRef.current = null;

      /*
       * 🔴 THE WRONG-DOOR CHECK APPLIES HERE TOO, AND THIS IS THE PATH THAT WOULD
       * QUIETLY MISS IT. Enrolment reaches a session without passing through
       * `signInAt`'s tail — the token pair comes straight back from
       * `/auth/mfa/enrol/finish` — so an operator enrolling at the teacher door
       * would otherwise be adopted unchecked.
       *
       * ⚠️ THE FACTOR STAYS ENROLLED. It was created on the server the moment the
       * code verified and this refusal does not undo it, which is correct: the
       * account now has the second factor it is required to have, and the next
       * sign-in at the right door will simply ask for a code. Discarding the
       * recovery codes is the cost, and they can be regenerated from Settings.
       */
      if (!doorAccepts(door, next.role)) {
        const refresh = getStaffRefreshToken();
        clearStaffSession();
        setSession(null);
        if (refresh) void endStaffSession(refresh);
        return { at: "wrong-door", door: doorFor(next.role) };
      }

      // See `awaitingRecoveryCodes`: this session is allowed to stay on the
      // door until the operator says they have written the codes down.
      setAwaitingRecoveryCodes(true);
      return { at: "enrolled", codes: result.recoveryCodes };
    },
    [adoptSession],
  );

  /** Called once the operator says they have saved the recovery codes. */
  const enterAfterEnrolment = useCallback(() => {
    setAwaitingRecoveryCodes(false);
    if (session) replace(homeFor(session.role));
  }, [session, replace]);

  /**
   * The second step, for every door.
   *
   * 🔴 IT CLEARS `pendingMfa` ONLY ON SUCCESS. A wrong code must leave the
   * challenge in place so the person can try the next 30-second code — throwing
   * the challenge away on a typo would send them back to the password form, and
   * the second factor would read as "my password stopped working".
   *
   * ⚠️ AN EXPIRED CHALLENGE (401 after five minutes) IS INDISTINGUISHABLE FROM A
   * WRONG CODE HERE, because `client.ts` discards response bodies. The screen
   * says both things in one sentence rather than guessing.
   *
   * 🔴 THE DOOR COMES FROM THE CHALLENGE, NOT FROM WHATEVER SCREEN IS MOUNTED.
   * This is the path the reported bug actually travelled: an operator typed a
   * password and a correct authenticator code at the school admin's address and
   * was signed in, because only the operator branch ever checked.
   */
  const submitMfaCode = useCallback(
    async (code: string): Promise<DoorOutcome> => {
      if (!pendingMfa)
        throw Object.assign(new Error("No sign-in in progress"), {
          status: 401,
        });

      const tokens = await completeStaffMfa(
        pendingMfa.mfaToken,
        code,
        pendingMfa.remember,
      );
      const outcome = finishSignInAtDoor(
        tokens,
        pendingMfa.door,
        pendingMfa.remember,
      );
      setPendingMfa(null);
      return outcome;
    },
    [pendingMfa, finishSignInAtDoor],
  );

  /** Back to the password form. Discards the assertion rather than keeping it warm. */
  const cancelMfa = useCallback(() => {
    setPendingMfa(null);
    /* 🔴 CLEARED ON THE WAY OUT. Backing out of an enrolment must not leave a
       live fifteen-minute token in memory for the next person at the laptop. */
    enrolmentTokenRef.current = null;
    enrolmentDoorRef.current = null;
    changeRef.current = null;
  }, []);

  /*
   * 🔴 THE PARENT'S SIGN-IN HAS TO REACH `session` TOO, OR IT LANDS NOWHERE.
   *
   * `mayNavigate` reads `session?.role`, and `#/parent` is gated on `PARENT`.
   * Writing only `parentSession` (localStorage, for `shared/api` to read) would
   * leave this component's role still `null`, so the effect above would bounce
   * a parent who had just verified a correct code straight back to the staff
   * sign-in form — with a valid token in storage. The store and this state are
   * written together, from one place, for the same reason `signIn` does it.
   */
  const verifyOtp = useCallback(
    async (phone: string, code: string) => {
      /*
       * 🔴 WHICHEVER DOOR SENT THE CODE IS THE DOOR THAT CHECKS IT, and the
       * question is asked of the SDK rather than of the config. A build can be
       * configured for Firebase while this particular code came from WhatsApp —
       * a parent who started the flow before a deploy, or a number Firebase
       * refused — and confirming a WhatsApp code against Firebase fails with a
       * refusal the parent cannot act on. `hasPendingCode` answers "is there a
       * live Firebase confirmation for THIS number", which is the real question.
       */
      const rail = railFor({
        firebaseConfigured: firebaseConfigured(),
        hasPendingCode: hasPendingCode(phone),
      });

      /*
       * 🔴 THE THIRD ANSWER, WHICH IS WHAT WAS MISSING AND WHAT BROKE PARENT
       * SIGN-IN. This read `hasPendingCode(phone) ? confirm : verifyParentOtp`,
       * so a Firebase build with no handle posted the code to the WhatsApp
       * verifier — which on a Firebase build can never hold a challenge,
       * because `/auth/parent/otp/request` is never called there. The endpoint
       * answered its deliberately generic 401, the screen rendered "that code
       * did not work", and the parent retried until the ten-a-minute IP limit
       * turned it into a 429. The code was never the problem.
       *
       * ⚠️ 410 RATHER THAN 401, SO THE SCREEN CAN SAY SOMETHING DIFFERENT. Gone
       * is exactly right: there was a confirmation and there is not one now.
       * A 401 would be the fourth thing hiding behind one sentence, and this is
       * the one case where the honest instruction ("ask for a new code") is not
       * an enumeration signal — it says nothing about whether the number is
       * registered, only that this browser is no longer holding a challenge.
       */
      if (rail === "expired") {
        throw Object.assign(
          new Error("No code is outstanding for this number"),
          {
            status: 410,
          },
        );
      }

      const tokens =
        rail === "firebase"
          ? await confirmPhoneCode(code)
          : await verifyParentOtp(phone, code);

      /*
       * ⚠️ FAIL CLOSED ON THE CLAIM RATHER THAN ASSUMING THE ROUTE'S NAME. The
       * endpoint is the parent one, so `PARENT` is overwhelmingly what comes
       * back — but the role is what decides which tier renders, and taking it
       * on trust from the URL we happened to call is how a client ends up
       * asserting its own privileges. Same rule as `sessionFromTokens`.
       */
      const claims = decodeJwtClaims(tokens.accessToken);
      if (claims?.role !== "PARENT") {
        throw Object.assign(new Error("Unusable session token"), {
          status: 401,
        });
      }

      setParentSession(tokens);
      setSession({ role: "PARENT", token: tokens.accessToken });
      /* P2-03 — the number has done its job; it does not outlive the sign-in. */
      clearOtpHop();
      // `replace`, not `navigate` — back must not return to a spent code form.
      replace("parent");
    },
    [replace],
  );

  /*
   * ✅ X4 — THE FEATURE WAS BUILT, AUDITED AND UNREACHABLE.
   *
   * `POST /auth/impersonate` shipped with `ImpersonationService`, its audit
   * derivation and eleven tests, and the banner below rendered from
   * `session.impersonatorId` — a field nothing in the product could ever set,
   * because no screen called the endpoint and nothing could produce the `userId`
   * it needs. `GET /superadmin/schools/:id/staff` is the other half.
   */
  const actAs = useCallback(
    async (userId: string, reason: string) => {
      const grant = await startImpersonation(
        { userId, reason },
        { token: getStaffToken() ?? "" },
      );

      /*
       * 🔴 FAIL CLOSED IF THE GRANT DOES NOT CARRY `imp`. `beginImpersonation`
       * refuses a token without the operator's id in it, because swapping into
       * one would put the operator inside another account with NO BANNER — the
       * silent view-as §8 forbids, arriving through the door built to prevent it.
       */
      const next = beginImpersonation(grant.accessToken);
      if (!next)
        throw Object.assign(new Error("Unusable impersonation token"), {
          status: 401,
        });

      setSession({
        role: next.role,
        token: next.accessToken,
        impersonatorId: next.impersonatorId,
      });
      /*
       * ⚠️ `replace`, NOT `navigate`, AND INTO THE IMPERSONATED TIER'S HOME.
       * Staying on `#/platform/schools` would render the operator's own screen
       * with somebody else's token, so every panel on it would 403 at once —
       * which reads as the impersonation having failed rather than having
       * worked. Back must not return there either: the platform route is not
       * one this session may see.
       */
      replace(homeFor(next.role));
    },
    [replace],
  );

  /*
   * 🔴 CHANGE 19 §6 — ASK THE SERVER WHETHER THIS SESSION IS GATED, ONCE PER
   * SESSION.
   *
   * ⚠️ IT RUNS FOR EVERY ROLE RATHER THAN ONLY FOR `ADMIN`, WHICH IS ONE EXTRA
   * REQUEST AND IS WORTH IT. The rule about *who* is gated lives on the server —
   * `TermsStatus.required` folds the role in — and duplicating it here is how
   * the two drift the day the gate is widened or narrowed. The route is cheap
   * and it is issued once, on sign-in.
   *
   * ⚠️ A FAILURE LEAVES THE GATE DOWN. The server refuses the dashboard anyway
   * if the terms really are outstanding, so the worst case of a failed check is
   * a 403 an admin can retry — not a person locked out of a product because a
   * request timed out.
   */
  useEffect(() => {
    if (session === null) {
      setTermsPending(false);
      return;
    }
    const controller = new AbortController();
    getTerms({ token: session.token, signal: controller.signal })
      .then((status) => {
        setTermsPending(status.required);
      })
      .catch(() => {
        setTermsPending(false);
      });
    return () => {
      controller.abort();
    };
  }, [session]);

  /**
   * 🔴 THE ONLY THING THAT ENDS A SESSION, NOW THAT ONE SURVIVES A RELOAD.
   * Everything else this file does to `session` is a redirect; this is the exit.
   *
   * 🔴 LOCAL FIRST, UNCONDITIONALLY, AND THE NETWORK CALL IS NOT AWAITED.
   * A sign-out that only works when the wifi does is a sign-out that leaves
   * the previous teacher's dashboard open on a school laptop. `endStaffSession`
   * swallows its own failure by design.
   *
   * 🔴 BOTH STORES ARE CLEARED, NOT ONLY THE ONE THIS SESSION CAME FROM, AND
   * THAT WAS A LIVE BUG THE MOMENT THE RESTORE LANDED. This used to clear the
   * staff store alone — harmless while nothing read the parent pair back at
   * boot, and wrong the instant something did: a parent pressing Sign out kept a
   * valid pair under `kalqy.parent`, so the next load restored the session they
   * had just ended. The same browser can legitimately hold both (a teacher who
   * is also a parent at the school), and "sign out" cannot honestly mean "sign
   * out of one of the two accounts you are holding".
   *
   * ⚠️ AND EVERY REFRESH TOKEN HELD IS REVOKED, NOT JUST THE FIRST. Each is a
   * thirty-day credential with its own `RefreshSession` row; leaving one live on
   * the server because the local copy is gone is the half-measure ADR-026's
   * one-row-per-token model exists to make unnecessary.
   */
  const signOut = useCallback(() => {
    // Read before clearing — `clearStaffSession` is what makes them unreadable.
    const revoke = [getStaffRefreshToken(), getParentRefreshToken()].filter(
      (token): token is string => token !== null,
    );

    clearStaffSession();
    clearParentSession();
    setSession(null);
    setAwaitingRecoveryCodes(false);
    for (const token of revoke) void endStaffSession(token);
    replace("landing");
  }, [replace]);

  const stopActingAs = useCallback(() => {
    /*
     * 🔴 LOCAL FIRST AND UNCONDITIONALLY — `signOut`'s rule, for the same
     * reason. `POST /auth/impersonate/end` only RECORDS the intent (an access
     * token cannot be revoked, §8.1), so making the swap-back wait on it would
     * strand an operator inside somebody else's account whenever the wifi is
     * poor. The audit row is fired and not awaited.
     */
    const impersonatedToken = getStaffToken();
    const operator = endImpersonationLocally();

    if (impersonatedToken)
      void endImpersonation({ token: impersonatedToken }).catch(
        () => undefined,
      );

    if (!operator) {
      /*
       * ⚠️ NOTHING TO RETURN TO — SIGN OUT RATHER THAN CARRY ON. This is only
       * reachable if the operator's stashed session was cleared underneath us,
       * and the alternative is leaving somebody holding an impersonated
       * credential with no banner and no way back. An unexpected state on the
       * one feature that changes who you are resolves to the safe end.
       */
      signOut();
      return;
    }

    setSession({
      role: operator.role,
      token: operator.accessToken,
      impersonatorId: operator.impersonatorId,
    });
    replace(homeFor(operator.role));
  }, [replace, signOut]);

  /*
   * 🔴 THE PARENT'S NUMBER LIVES HERE, IN MEMORY, FOR ONE HOP.
   *
   * A4 masks it to the last two digits and never echoes the rest, so the two
   * screens need a channel between them. It is deliberately NOT the hash: a
   * phone number in a URL is a phone number in browser history, in a bookmark,
   * and in whatever a shared classroom laptop syncs — for a number that
   * identifies a specific child's parent.
   */
  const [phone, setPhoneState] = useState(
    () => loadOtpHop(Date.now())?.phone ?? "",
  );
  /**
   * ✅ P2-03 — WHEN THE CURRENT CODE WAS ASKED FOR, and the number, kept in THIS
   * TAB's `sessionStorage` so a reload of the code screen does not strand a
   * parent on an empty "Sent to +91 ••••" line. Still never the hash: the reason
   * above stands, and `otpResend.ts` records it again where the write happens.
   */
  const [otpSentAt, setOtpSentAt] = useState<number | null>(
    () => loadOtpHop(Date.now())?.sentAt ?? null,
  );
  const setPhone = useCallback((next: string): void => {
    setPhoneState(next);
  }, []);
  const markOtpSent = useCallback((next: string): void => {
    const at = Date.now();
    setPhoneState(next);
    setOtpSentAt(at);
    saveOtpHop({ phone: next, sentAt: at });
  }, []);

  /*
   * 🔴 THE REDIRECT IS AN EFFECT, NOT A RENDER-TIME RETURN.
   *
   * Rendering a different route than the hash says would put the URL and the
   * screen out of step — the back button would then return to a page that
   * immediately bounced again, which reads as a frozen browser. Changing the
   * hash and letting the router re-run keeps one source of truth.
   */
  const allowed = mayNavigate(route, session?.role ?? null);

  /*
   * A6 — WHICH FRONT DOOR THIS HOSTNAME IS.
   *
   * 🔴 IT DECIDES A DEFAULT, NOT A PERMISSION. `operator.kalqy.app` opening on
   * the marketing page or on a picker whose largest tile is a play button for a
   * four-year-old is the wrong screen for that address; that is the whole of
   * what this changes. Every route below is still gated by `mayNavigate` and
   * every endpoint behind them by `@Roles`, on both hostnames identically —
   * `shared/lib/host.ts` says so at length, because a separation this visible
   * invites the belief that it is the control.
   *
   * ⚠️ NEITHER SCHOOL DOOR IS REDIRECTED, EVEN HERE. `staff-login` and
   * `school-login` are the escape hatch for somebody who arrived at the operator
   * address with a school account, and the school app is a hostname this bundle
   * cannot reliably construct — guessing it wrong would strand them on a 404
   * instead of a form that works. Both carry no `roles`, so `mayNavigate` lets
   * them through on either hostname and this effect never fires for them.
   */
  const operatorDoor = isOperatorHost();
  const misplaced =
    operatorDoor &&
    session === null &&
    (route.id === "landing" || route.id === "start");

  /*
   * 🔴 A PARENT NEVER RENDERS G1–G4, AND `mayNavigate` CANNOT BE THE THING THAT
   * SAYS SO. Those four routes carry no `roles` on purpose — they are every
   * signed-in adult's own settings, and a role list would be a claim that some
   * tier owns them — so a parent arriving at `#/settings/account` from an old
   * bookmark, or from the Settings control before this change, passes the gate
   * and is shown a password form for an account that has no password.
   *
   * ⚠️ IT IS A REDIRECT RATHER THAN AN EMPTY SCREEN OR A REFUSAL, because there
   * IS a right answer: Privacy, which since ADR-051 carries this tier's
   * preferences as well as its consent — including the sentence explaining why
   * there is no password here to change.
   */
  const parentAtStaffSettings =
    session?.role === "PARENT" && route.id.startsWith("settings-");

  /*
   * 🔴 A SIGNED-IN PERSON ON A DOOR IS SENT HOME, AND THIS IS THE OTHER HALF OF
   * THE "it logs me out" REPORT.
   *
   * Restoring the session on boot fixes the reload. It does not fix the back
   * button, because the screen back lands on is usually an ENTRY route —
   * `parent/signin`, `start`, `#/` — and those carry no `roles` on purpose, so
   * `mayNavigate` waves them through and the app renders a sign-in form at
   * somebody who is signed in. Nobody can read that as anything but a lost
   * session; it looks identical to one.
   *
   * ⚠️ IT IS A REDIRECT RATHER THAN A REFUSAL, and it is `replace` rather than
   * `navigate` — `isEntryRoute` in `routes.ts` carries the reasoning for both,
   * and for which routes are deliberately NOT in the list (the ones that carry a
   * credential in the hash).
   *
   * 🔴 THE ONE EXEMPTION IS THE RECOVERY-CODES SCREEN. See
   * `awaitingRecoveryCodes`: an operator finishing a forced enrolment holds a
   * live session on the operator door on purpose, and bouncing them off it would
   * destroy the only copy of the codes.
   */
  const home = session === null ? null : homeFor(session.role);
  const entryBounce =
    /*
     * ⚠️ `home === "landing"` IS THE ONE CASE THAT MUST NOT BOUNCE, AND IT IS
     * NOT HYPOTHETICAL THINKING — it is `homeFor`'s documented fail-closed
     * default: *"a role added later lands on the landing page rather than
     * guessing"*. The landing page is itself an entry route, so bouncing there
     * would replace the hash with the one it just refused: the effect's
     * dependencies would be unchanged, so nothing would re-run, and the render
     * guard below would return `null` forever. A blank screen is a worse answer
     * than the marketing page for a role nobody has taught this build about —
     * and it is the answer the code would give without this clause.
     */
    home !== null &&
    home !== "landing" &&
    !awaitingRecoveryCodes &&
    isEntryRoute(route.id)
      ? home
      : null;

  useEffect(() => {
    if (misplaced) {
      replace("operator-login");
      return;
    }
    if (parentAtStaffSettings) {
      replace("parent");
      return;
    }
    if (entryBounce) {
      replace(entryBounce);
      return;
    }
    if (allowed) return;
    /*
     * ⚠️ AN UNAUTHENTICATED USER GOES TO SIGN-IN; AN AUTHENTICATED ONE GOES
     * HOME. Sending a signed-in teacher who typed `#/platform` to the login
     * screen would tell them their session had expired, which is both false and
     * the kind of thing that makes people stop trusting a product.
     *
     * ✅ AND "SIGN-IN" IS NOW THE DOOR PICKER RATHER THAN THE TEACHER'S FORM.
     * This used to land everyone on `staff-login`, which was defensible only
     * while that route was the address for all staff. It is the TEACHER door —
     * now named `teacher/signin`, which says so — and a school admin who typed
     * `#/school` from a bookmark with an expired session would have been shown a
     * teacher's password field with no way onward except guessing another URL.
     * `start` exists to route exactly that person, and it carries every door.
     */
    replace(
      session
        ? homeFor(session.role)
        : operatorDoor
          ? "operator-login"
          : "start",
    );
  }, [
    allowed,
    entryBounce,
    misplaced,
    parentAtStaffSettings,
    operatorDoor,
    session,
    replace,
  ]);

  if (!allowed || misplaced || parentAtStaffSettings || entryBounce !== null)
    return null;

  /*
   * 🔴 CHANGE 19 §6 — THE DASHBOARD DOES NOT OPEN UNTIL THE TERMS ARE ACCEPTED,
   * AND THIS IS THE SCREEN THAT ASKS. `TermsGuard` on the server is what makes
   * it true — every route but the terms pair is refused for an un-accepted
   * ADMIN — so this is not the enforcement; it is the reason the refusal is
   * something a person can act on rather than a blank 403.
   *
   * 🔴 IT REPLACES THE ROUTE RATHER THAN COVERING IT. A modal over a dashboard
   * implies the dashboard underneath works, and it does not: every request it
   * makes comes back 403 until this is done.
   *
   * ⚠️ THE SIGN-IN AND DOOR ROUTES ARE NOT GATED, and they cannot be — a session
   * exists by the time this runs, so those routes are already unreachable, but a
   * gate that swallowed them would strand somebody mid-sign-out.
   */
  if (termsPending && session !== null) {
    return (
      <TermsGate
        token={session.token}
        onAccepted={() => {
          setTermsPending(false);
        }}
        onSignOut={signOut}
      />
    );
  }

  return renderRoute(
    route.id,
    session,
    navigate,
    { phone, setPhone, sentAt: otpSentAt, markSent: markOtpSent },
    {
      signIn,
      signInAsSchoolAdmin,
      signInAsOperator,
      changePassword,
      adoptClaimSession,
      submitMfaCode,
      cancelMfa,
      startEnrolment,
      finishEnrolment,
      enterAfterEnrolment,
      mfaPending: pendingMfa !== null,
      signOut,
      actAs,
      stopActingAs,
      // The screen holds the code; the number is this component's, so it is
      // bound here rather than threaded back through the screen.
      verifyOtp: (code: string) => verifyOtp(phone, code),
    },
    lang,
    gameFilter,
    openGames,
  );
}

/**
 * ✅ THE SIDEBAR TITLES BEING SINGLE STRINGS IS NO LONGER A GAP. It was:
 * `RouteDef.title` is one string, so `navFor` returned English labels while
 * every screen behind those links was bilingual, making the nav the one
 * untranslated surface in the product. ADR-044 removed the second language, so
 * the single string is now simply correct.
 */

interface PhoneHop {
  phone: string;
  setPhone: (phone: string) => void;
  /** P2-03 — when the current code was asked for, for the resend countdown. */
  sentAt: number | null;
  /** Record a code as ASKED FOR (the request was accepted), and remember the number. */
  markSent: (phone: string) => void;
}

interface SessionActions {
  /**
   * The three doors, one per tier. Same endpoint, same generic refusal, no role
   * asserted by the client; they differ only in which tier may complete a
   * sign-in at that address, and a wrong-tier account is signed straight back
   * out rather than left holding a session its bookmark created.
   *
   * ⚠️ THEY RESOLVE `'mfa'` AND `'enrol'` WITHOUT NAVIGATING. A `Promise<void>`
   * here would leave the sign-in button spinning forever for every enrolled
   * account, because resolving used to mean "the router has already replaced
   * this screen".
   */
  signIn: (
    email: string,
    password: string,
    remember: boolean,
  ) => Promise<StaffSignIn>;
  signInAsSchoolAdmin: (
    email: string,
    password: string,
    remember: boolean,
  ) => Promise<StaffSignIn>;
  signInAsOperator: (email: string, password: string) => Promise<StaffSignIn>;
  /** P3-16 — the forced change; resolves with how the sign-in continues. */
  changePassword: (newPassword: string) => Promise<StaffSignIn>;
  /** P2-02 — adopt the session-only pair a claimed invitation came back with. */
  adoptClaimSession: (tokens: StaffTokens) => void;
  /**
   * The compulsory first enrolment, before any session exists.
   *
   * 🔴 TWO CALLBACKS RATHER THAN ONE, BECAUSE IT IS TWO REQUESTS AND THE GAP
   * BETWEEN THEM IS THE SAFETY PROPERTY. `startEnrolment` mints a secret and
   * changes nothing; `finishEnrolment` proves the phone holds it and is what
   * turns the factor on. A single call that did both would enable a factor
   * nobody had scanned.
   */
  startEnrolment: () => Promise<MfaEnrolment>;
  /**
   * Resolves with the recovery codes — the only copy that will ever exist — or
   * with `wrong-door`, because enrolment is the one path to a session that does
   * not pass through a door's tail and would otherwise skip the check.
   */
  finishEnrolment: (code: string) => Promise<EnrolmentOutcome>;
  /** Called once the operator confirms they have saved the codes. */
  enterAfterEnrolment: () => void;
  /**
   * The second factor's step, for both doors.
   *
   * 🔴 THE SCREEN NEVER HOLDS THE `mfaToken` AND NEVER HOLDS THE PASSWORD. The
   * router keeps the five-minute assertion; the screen has a text box. That is
   * what stops a credential living in a component while somebody unlocks a phone.
   */
  submitMfaCode: (code: string) => Promise<DoorOutcome>;
  /** Back to the password form, discarding the assertion. */
  cancelMfa: () => void;
  /** Whether a challenge is open. The screen renders the code step from this. */
  mfaPending: boolean;
  /** §9.1 — the parent's second step. Resolves once the session exists. */
  verifyOtp: (code: string) => Promise<void>;
  signOut: () => void;
  /**
   * X4 — begin and end acting as a member of staff.
   *
   * 🔴 THEY LIVE BESIDE `signIn` AND `signOut` BECAUSE THEY ARE THE SAME KIND OF
   * ACT: each replaces the credential the whole app is holding. Putting them on
   * a screen would mean a table cell could change who the browser is while the
   * router carried on drawing the previous tier's sidebar.
   */
  actAs: (userId: string, reason: string) => Promise<void>;
  stopActingAs: () => void;
}

/**
 * ⚠️ `openGames` IS THREADED THROUGH RATHER THAN LIVING IN THE SCREEN, because
 * the two ends are on different routes. A game card is drawn by E1 (`school`)
 * and the log it opens is `school-games`, so the chosen game cannot be state
 * inside either one — it is handed up here, exactly as `consentClassId` is.
 *
 * ⚠️ IT SEEDS A FILTER AND DOES NOT SURVIVE A REFRESH, WHICH IS THE HONEST
 * LIMIT OF A HASH ROUTER WITH NO QUERY STRING. `normalisePath` does not strip
 * `?…`, so `#/school/games?game=sky-quest` would match no route at all. A
 * reloaded page opens on "All games" — a wider view than asked for, never a
 * narrower one, and the filter is right there to set again.
 */
function renderRoute(
  id: RouteId,
  session: Session | null,
  navigate: (id: RouteId) => void,
  otp: PhoneHop,
  actions: SessionActions,
  lang: Lang,
  gameFilter: string | undefined,
  openGames: (game?: string) => void,
) {
  /*
   * `auth` is the shape every dashboard screen already takes — the caller
   * supplies the token and the module stores nothing, exactly as `teaching.ts`,
   * `parent.ts` and `school.ts` were written to work.
   */
  const auth = session ? { token: session.token } : { token: "" };

  /*
   * ✅ THE CONSOLE REGISTRY GOES FIRST. A route a redesign workstream has
   * registered (features/console/<tier>/index.ts) is served from there; every
   * other route falls through to the switch below exactly as before.
   */
  const consoleScreen = consoleScreenFor(id);
  if (consoleScreen) {
    const Screen = consoleScreen.component;
    /*
     * Console screens are lazy chunks (features/console/lazyScreen.ts). The
     * shell and sidebar render at once; only the page body waits, and a page
     * that fails (a chunk that did not download) is caught per page.
     */
    const screen = (
      <ConsoleScreenBoundary key={id}>
        <Suspense fallback={<ConsoleScreenLoading />}>
          <Screen
            routeId={id}
            auth={auth}
            role={session?.role ?? null}
            lang={lang}
            navigate={navigate}
            signOut={actions.signOut}
            actAs={session?.role === "SUPER_ADMIN" ? actions.actAs : undefined}
          />
        </Suspense>
      </ConsoleScreenBoundary>
    );
    if (consoleScreen.frame === "bare") return screen;
    return (
      <Shell
        routeId={id}
        session={session}
        navigate={navigate}
        actions={actions}
        nav={consoleScreen.nav}
        contentMaxWidthClass={consoleScreen.contentMaxWidthClass}
      >
        {screen}
      </Shell>
    );
  }

  switch (id) {
    case "landing":
      /*
       * The website. Everything a school needs to decide whether to look
       * further, and one control — sign in — which opens the picker.
       */
      return (
        <WelcomeScreen
          /* P2-01 — the website's "Sign in" opens the staff door, not the
             child's picker; the door's tabs reach the school admin's. */
          onSignIn={() => {
            navigate("staff-login");
          }}
        />
      );

    case "start":
      /*
       * A1's three tiles, moved one hash in. The child's is the largest and
       * carries an icon, so it is reachable without reading the label under it.
       */
      return (
        <LandingScreen
          onChild={() => {
            navigate("kid");
          }}
          /*
           * ✅ THE ADULTS' SIGN-IN HAPPENS HERE NOW, IN THE PANEL BESIDE THE
           * PLAY CARD — and it is `StaffLoginScreen` and `ParentPhoneScreen`
           * themselves, so these are the same handlers `staff-login` and
           * `parent-phone` are given below. Anything less than the full set
           * would leave a step of those screens unrendered inside the panel:
           * an enrolled teacher's button spinning on a challenge, or a parent's
           * number going nowhere.
           *
           * 🔴 THE ROLE CHOOSER IN THAT PANEL IS NOT SENT ANYWHERE. It decides
           * which of the two forms is shown; `signIn` still POSTs the same two
           * fields and `finishSignInAtDoor` still signs a mismatched account
           * back out. See `EntryScreens`' own docblock — the distinction is the
           * difference between a form switch and a privilege claim.
           */
          onSignIn={actions.signIn}
          /*
           * 🔴 THE CHOOSER'S THIRD OPTION GETS ITS OWN FUNCTION, NOT A FLAG.
           * `signInAsSchoolAdmin` differs from `signIn` in the `Door` it hands
           * `finishSignInAtDoor` — which is the check that signs a teacher back
           * out of the admin tier and an operator back out of both. One handler
           * for both options would make the two doors one again, which is the
           * bug the school door exists to close.
           */
          onSignInAsSchoolAdmin={actions.signInAsSchoolAdmin}
          onChangePassword={actions.changePassword}
          onSubmitCode={actions.submitMfaCode}
          onCancelCode={actions.cancelMfa}
          onStartEnrolment={actions.startEnrolment}
          onFinishEnrolment={actions.finishEnrolment}
          onEnterAfterEnrolment={actions.enterAfterEnrolment}
          /* Where a WRONG-DOOR refusal points — including the operator door,
             which is not in the chooser and is on another hostname. */
          onDoor={(door) => {
            navigate(DOOR_ROUTE[door]);
          }}
          onSchoolAdmin={() => {
            navigate("school-login");
          }}
          onStaff={() => {
            navigate("staff-login");
          }}
          /* A3's half, wired exactly as `parent-phone` is — see that case. */
          parentViaSms={firebaseConfigured()}
          onRequestParentCode={async (next) => {
            otp.setPhone(next);
            if (firebaseConfigured()) await sendPhoneCode(next);
            else await requestParentOtp(next);
            otp.markSent(next);
          }}
          onParentSubmit={(next) => {
            otp.setPhone(next);
            navigate("parent-otp");
          }}
        />
      );

    case "parent-phone":
      /*
       * ✅ WIRED — it POSTs `/auth/parent/otp/request` and then advances.
       *
       * 🔴 IT ADVANCES WHETHER OR NOT THE NUMBER IS REGISTERED, AND THAT IS
       * A3's RULE RATHER THAN A SHORTCUT. The endpoint answers an identical
       * generic 200 for a registered number, a stranger's and gibberish — a
       * screen that only moved on for a real one would rebuild, in the UI, the
       * phone-enumeration oracle the endpoint exists to prevent. The number
       * belongs to the parent of an identified child.
       *
       * ⚠️ WHICH IS WHY `.catch()` STILL ADVANCES TOO. A transport failure is
       * the screen's to report (it shows "wait a minute" on a 429), but it must
       * not become a signal about the number. `onRequestCode` rejecting is
       * handled inside the screen; the hop happens either way.
       */
      return (
        <ParentPhoneScreen
          /* 🔴 WHICH RAIL THE SMS TAKES, SO THE SCREEN CAN SAY SO HONESTLY. "We
             will send the code on WhatsApp" is wrong on a Firebase build, and a
             parent who goes looking in WhatsApp for a code that arrived as an
             SMS concludes it never came. */
          viaSms={firebaseConfigured()}
          onRequestCode={async (next) => {
            otp.setPhone(next);
            /*
             * ⚠️ FIREBASE FIRST WHERE IT IS CONFIGURED, AND NO SILENT FALLBACK
             * TO WHATSAPP. Sending through both would mean two codes, one of
             * which cannot work — and `otp_login` has never been approved by
             * Meta, so the WhatsApp one is the one that would not arrive. A
             * build picks a rail; the failure of that rail is a failure the
             * screen reports.
             */
            if (firebaseConfigured()) await sendPhoneCode(next);
            else await requestParentOtp(next);
            otp.markSent(next);
          }}
          onSubmit={(next) => {
            otp.setPhone(next);
            navigate("parent-otp");
          }}
        />
      );

    case "parent-otp":
      /*
       * ✅ WIRED — `/auth/parent/otp/verify` mints the parent's access token.
       *
       * ⚠️ THE PARENT SESSION IS `localStorage`, WHERE THE STAFF ONE IS MEMORY,
       * AND THE ASYMMETRY IS DELIBERATE. A teacher's token dies with the tab
       * because a classroom laptop is shared and the next class must not open
       * the previous teacher's dashboard. A parent is on their OWN phone,
       * reading for thirty seconds, and there is no refresh route for them —
       * making them re-do a WhatsApp round trip on every visit is how a parent
       * stops opening it at all. `parentSession.ts` owns that decision.
       */
      return (
        <OtpScreen
          phone={otp.phone}
          viaSms={firebaseConfigured()}
          onVerify={actions.verifyOtp}
          /* P2-03 — "Resend code in 0:42", then the same request the phone
             screen made, on the same rail, to the same number. */
          sentAt={otp.sentAt}
          onResend={
            otp.phone === ""
              ? undefined
              : async () => {
                  if (firebaseConfigured()) await sendPhoneCode(otp.phone);
                  else await requestParentOtp(otp.phone);
                  otp.markSent(otp.phone);
                }
          }
        />
      );

    /*
     * `#/connect` was the website's second enquiry form. It and `#/contact-sales`
     * both posted a `DemoRequest` to `/demo-requests`; the site now uses
     * contact-sales only, and the effect at the top of `AppRouter` forwards old
     * links and bookmarks there. Nothing renders for the one frame in between.
     */
    case "connect":
      return null;

    case "staff-login":
      /*
       * ✅ WIRED, AND NOW THE TEACHER'S DOOR SPECIFICALLY. The screen POSTs to
       * `/auth/login`, `signIn` stores the pair and `replace`s to
       * `homeFor(role)` — so the role that decides the landing screen still
       * comes from the account, never from anything the form asserted. What
       * changed is that an account belonging to another tier is signed back out
       * and pointed at its own door instead of landing here.
       *
       * ⚠️ A COORDINATOR BELONGS HERE AND A PRINCIPAL DOES NOT, and neither fact
       * is written in this file: `doorAccepts` reads `homeFor`, which is the one
       * place that decides which tier a role sits on (S6 scopes a coordinator to
       * assigned classes, so the teacher tier is the screen their scope fits).
       *
       * 🔴 THE ROUTER DOES NOT CATCH. `signIn` rejects and the SCREEN renders the
       * refusal, because the screen is the only place that knows the difference
       * between "wrong password", "too many attempts" and "no network" matters
       * to the person looking at it. A router-level error would be a blank page
       * with a console line.
       */
      return (
        <StaffLoginScreen
          door="teacher"
          onSignIn={actions.signIn}
          onChangePassword={actions.changePassword}
          /* Any staff account may enrol, so every door needs the code step too —
             without it, an enrolled teacher's sign-in button spins forever on a
             challenge nothing renders. */
          onSubmitCode={actions.submitMfaCode}
          onCancelCode={actions.cancelMfa}
          /* An operator can still reach an enrolment challenge here, because the
             server decides that from the account and not from the address. They
             are refused after it completes, not before — so the door still has
             to be able to render the enrolment rather than spin. */
          onStartEnrolment={actions.startEnrolment}
          onFinishEnrolment={actions.finishEnrolment}
          onEnterAfterEnrolment={actions.enterAfterEnrolment}
          onDoor={(door) => {
            navigate(DOOR_ROUTE[door]);
          }}
        />
      );

    /*
     * ✅ THE SCHOOL ADMIN'S DOOR — the route that did not exist, and whose
     * absence is the whole of the reported bug. `#/signin` was one form for
     * every staff role, so a platform operator's credentials and authenticator
     * code worked at the address a school admin was given.
     *
     * ⚠️ IT IS THE SAME COMPONENT WITH A DIFFERENT `door`, NOT A SECOND SCREEN.
     * The two differ in a title, a sentence and one value; a copy would be two
     * places for the second factor's step to drift apart.
     */
    case "school-login":
      return (
        <StaffLoginScreen
          door="school"
          onSignIn={actions.signInAsSchoolAdmin}
          onChangePassword={actions.changePassword}
          onSubmitCode={actions.submitMfaCode}
          onCancelCode={actions.cancelMfa}
          onStartEnrolment={actions.startEnrolment}
          onFinishEnrolment={actions.finishEnrolment}
          onEnterAfterEnrolment={actions.enterAfterEnrolment}
          onDoor={(door) => {
            navigate(DOOR_ROUTE[door]);
          }}
          /*
           * ⛔ NO SIGN-UP LINK ANY MORE (P1-14, D7). Kalqy creates a school's
           * first login after approval; the server answers the sign-up routes
           * 404 unless `SCHOOL_SELF_SIGNUP_ENABLED` is set, and the two routes
           * below stay only for links already sent.
           */
        />
      );

    /*
     * 🔴 A SCHOOL ASKING TO EXIST — AND THIS SCREEN CREATES NOTHING.
     *
     * `admin-panel.md` §3 says *"a school cannot mint its own first admin"*, and
     * the operator-only provisioning path is untouched. What this form does is
     * ask the server for a single-use link; the school, its first ADMIN and its
     * subscription are written by the claim screen below, which needs that link
     * back. So a public route produces rows that expire rather than staff logins.
     */
    case "school-signup":
      return (
        <SchoolSignUpScreen
          onSignIn={() => {
            navigate("school-login");
          }}
        />
      );

    case "school-signup-claim": {
      const token = signupTokenFrom(window.location.hash);
      /*
       * ⚠️ A BARE `#/signup` WITH NO TOKEN IS THE FORM, not an error — the same
       * choice `invite` makes. Somebody who truncated the link is best served by
       * the screen that can send them another one.
       */
      if (token === null) {
        return (
          <SchoolSignUpScreen
            onSignIn={() => {
              navigate("school-login");
            }}
          />
        );
      }
      return (
        <SchoolSignUpClaimScreen
          token={token}
          onDone={() => {
            navigate("school-login");
          }}
        />
      );
    }

    /*
     * ✅ A6 — THE OPERATOR'S DOOR, ON ITS OWN HOSTNAME AND ITS OWN HASH.
     *
     * 🔴 PUBLIC, LIKE EVERY SIGN-IN SCREEN. `routes.ts` gives it no `roles`,
     * because a role list on the page that creates the session would make it
     * reachable only by somebody who already had one.
     *
     * ⚠️ THE SCHOOL DOOR IS OFFERED FROM HERE AND NOT THE OTHER WAY ROUND. A2
     * already names operators in its subtitle and works for them; this link
     * exists for the person who arrived at the operator address by mistake, and
     * an equivalent link on A2 would advertise an address a school has no reason
     * to know.
     */
    case "operator-login":
      return (
        <OperatorLoginScreen
          onSignIn={actions.signInAsOperator}
          onChangePassword={actions.changePassword}
          /* The second factor's step. The screen gets a callback and a text
             box; the router keeps the five-minute assertion, so no credential
             lives in a component while somebody unlocks a phone. */
          onSubmitCode={actions.submitMfaCode}
          onCancelCode={actions.cancelMfa}
          /* Compulsory for operators, so the first factor is created here —
             Settings is behind the sign-in that would otherwise refuse them. */
          onStartEnrolment={actions.startEnrolment}
          onFinishEnrolment={actions.finishEnrolment}
          onEnterAfterEnrolment={actions.enterAfterEnrolment}
          /*
           * ⚠️ IT NOW TAKES THE DOOR RATHER THAN ASSUMING THE STAFF ONE. A school
           * account that wanders in is pointed at whichever of the two school
           * doors is actually theirs — sending a school admin to the teacher form
           * would be a second wrong address after the first.
           */
          onDoor={(door) => {
            navigate(DOOR_ROUTE[door]);
          }}
        />
      );

    /*
     * ✅ OD-100's MISSING SCREEN. `POST /auth/invite` and `/auth/invite/claim`
     * shipped tested and audited with NO CALLER ANYWHERE in the frontend — so
     * every invitation the product could issue pointed at a page that did not
     * exist, stranding E3's teacher provisioning and the operator-recovery path
     * together.
     *
     * 🔴 PUBLIC, AND IT MUST STAY PUBLIC. The invitee has no account to sign in
     * with; the token IS the authentication. It carries no `roles` in the table
     * for that reason.
     */
    case "invite": {
      const token = inviteTokenFrom(window.location.hash);
      /*
       * ✅ P1-16 — A BARE `#/invite` IS REDIRECTED TO `staff-login` BY THE EFFECT
       * NEAR THE TOP, rather than drawn here as a door with half its handlers
       * (an enrolled account's code step used to do nothing). Nothing renders for
       * the one frame in between — `connect`'s pattern.
       */
      if (token === null) return null;
      return (
        <InviteClaimScreen
          token={token}
          /* The door for the role just claimed — a school admin sent to the
             teacher door had their new, correct password refused there. */
          onDone={(claimed) => {
            navigate(signInRouteAfterClaim(claimed?.role));
          }}
          /* P2-02 — signed in on claim when the server allows it. */
          onSignedIn={actions.adoptClaimSession}
        />
      );
    }

    case "kid":
      /*
       * ✅ MOUNTED. This returned a placeholder reading *"The child's flow is
       * kidFlow.ts, not a route"* — true, and not a screen. `KidFlow` is that
       * state machine with its screens and its five real rounds attached.
       *
       * 🔴 ONE ROUTE, AND EVERYTHING INSIDE IT IS STATE. The hash stops here on
       * purpose: a four-year-old never navigates, a deep link into a child's
       * screen is meaningless (every screen depends on which child is playing),
       * and a router inside the flow would bring a BACK BUTTON — which mid-round
       * on a touchscreen is a child losing the round they were about to finish
       * (OD-49).
       */
      return (
        <Suspense fallback={<KidChunkLoading />}>
          <KidApp />
        </Suspense>
      );

    /*
     * 🔴 THE FREE GAME — PUBLIC, AND IT IS NOT THE CHILD'S TIER.
     *
     * No session is read, none is created, and nothing the player does is
     * recorded: `FreePlayApp` mounts one round of Sky Quest and writes nowhere.
     * That is the whole difference from `kid` above, and it is why this case sits
     * beside it rather than inside it — see the route's own note in `routes.ts`.
     */
    case "sky-quest-play":
      return (
        <Suspense fallback={<KidChunkLoading />}>
          <FreePlayApp />
        </Suspense>
      );

    /*
     * The four game pages — website pages, public, one per game. The route id is
     * the game id, so it is handed straight through.
     */
    case "sky-quest":
    case "turos-world":
    case "letter-lagoon":
    case "bridge-patterns":
      return (
        <GamePage
          /* Keyed by game, so moving between two game pages remounts the hero —
             its video element would otherwise keep playing the first game's loop. */
          key={id}
          gameId={id}
          onSignIn={() => {
            navigate("staff-login");
          }}
        />
      );

    case "for-schools":
      return (
        <ForSchoolsPage
          onSignIn={() => {
            navigate("staff-login");
          }}
        />
      );

    case "teacher":
      /*
       * 🔴 `flat`, NOT `sidebar` — AND `DashboardShell` HAD THE VARIANT BUILT
       * FOR THIS EXACT TIER, UNUSED.
       *
       * `information-architecture.md` §2: the teacher tier is *"a two-mode fork,
       * then a flat surface — one slot, no hunting"*, and `DashboardShell`'s own
       * header says `flat` is *"Teacher, after the D1 mode fork… deliberately
       * has no persistent chrome to hunt through"*. The sidebar rendered a
       * single item, "My classes", pointing at the page already open.
       *
       * ⚠️ THE COST OF THE OTHER CHOICE IS MEASURED IN TAPS DURING CLASS. §2:
       * *"merging them into one navigation costs taps during the 30-minute slot,
       * which is the moment adoption is won or lost."* Leaderboard and Reports
       * hang off the dashboard instead.
       */
      /*
       * 🔴 NO `Shell` AT ALL — THE TEACHER TIER RENDERS ITS OWN.
       *
       * It is the only tier with a sidebar whose items are REDUCER ACTIONS
       * rather than routes (`routes.ts` has exactly one teacher route, because
       * `teacherFlow` is the navigation). `DashboardShell` builds its nav from
       * `navFor(role)`, which can only produce links to routes — so the chrome
       * has to live where the dispatcher does. Wrapping it as well would put a
       * second header above a sidebar that already carries the class, the screen
       * and sign-out.
       */
      /*
       * ⚠️ THE ROLE TRAVELS WITH THE TOKEN, AND IT DECIDES WHAT IS DRAWN RATHER
       * THAN WHAT IS ALLOWED. Both a TEACHER and a COORDINATOR land here
       * (`routes.ts`), and S6 makes a coordinator a READ role — the homework
       * form is the one surface in this tier the server would refuse them, so
       * they are not shown it. The refusal itself is still the server's.
       */
      return (
        <TeacherApp
          auth={auth}
          lang={lang}
          role={session?.role ?? null}
          onSignOut={actions.signOut}
        />
      );

    /*
     * 🔴 SIX ROUTES, ONE MOUNTED COMPONENT. `ParentTier` holds the active child,
     * so it must not be torn down and refetched when a parent taps from Progress
     * to Attendance — `key` is deliberately absent and the switch falls through.
     */
    case "parent":
    case "parent-attendance":
    case "parent-achievements":
    case "parent-announcements":
    case "parent-messages":
    case "parent-reports":
      return (
        <Shell
          routeId={id}
          session={session}
          navigate={navigate}
          actions={actions}
          /*
           * ✅ THE SAME SIX ITEMS, IN THE LEFT SIDEBAR ON A SCREEN AND ON THE
           * THUMB BAR ON A PHONE. `nav="bottom"` put them across the bottom edge
           * at every width — six far-apart words along the one edge a laptop
           * reader never looks at, with no wordmark and no "Parent" tier label
           * anywhere, because both live in the sidebar this tier did not draw.
           * `adaptive` keeps the phone shape below `md` and gives every wider
           * window the sidebar the other adult tiers already have.
           */
          nav="adaptive"
        >
          <ParentTier
            routeId={id}
            navigate={navigate}
            auth={auth}
            lang={lang}
          />
        </Shell>
      );

    case "school":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <SchoolTier
            auth={auth}
            lang={lang}
            navigate={navigate}
            openGames={openGames}
          />
        </Shell>
      );

    case "school-reports":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <SchoolReports auth={auth} lang={lang} />
        </Shell>
      );

    /*
     * ✅ E7/E8 — EVERY CHILD IN THE SCHOOL, AND ONE CHILD'S REPORT.
     *
     * 🔴 NO `canWrite`, UNLIKE THE TWO CASES BELOW, AND THAT IS THE DESIGN
     * RATHER THAN AN OMISSION. E3 and E4 draw forms an ADMIN may submit and a
     * PRINCIPAL may not; this tier draws none for either — a child's class, roll
     * and band are E4's decisions and the teacher's mid-term controls are D5's.
     * A prop that both roles would pass the same value for is a prop that
     * invites a second answer later.
     *
     * ⚠️ THE OPEN CHILD IS STATE, NOT A ROUTE, exactly like S3's consent leaf
     * above. Putting a `playerId` in the hash would create a value a head
     * teacher can edit — and then this screen needs its own refusal story for an
     * id belonging to another school, duplicating the one `OwnPlayerGuard`
     * already enforces on every request the leaf makes.
     */
    case "school-students":
      return (
        <Shell
          routeId={id}
          session={session}
          navigate={navigate}
          actions={actions}
        >
          {/* 🔴 `canWrite` FROM THE ROLE, FOR E3's REASON BELOW: S6 makes a
              principal school read-only, so the enrol form is not drawn for
              them rather than drawn and refused. */}
          <SchoolStudentsTier
            auth={auth}
            lang={lang}
            canWrite={session?.role === "ADMIN"}
          />
        </Shell>
      );

    /*
     * ✅ E3/E4 — THE SCREEN THE SCHOOL TIER SHIPPED WITHOUT.
     *
     * 🔴 `canWrite` IS DERIVED FROM THE ROLE, AND IT IS NOT THE CONTROL. S6
     * makes a principal school read-only and the server enforces it on every
     * write this screen can issue (`StaffService.ownSchool`,
     * `ClassesService.writableSchool`). This decides whether a control is DRAWN,
     * which is D4's rule — a principal shown a disabled "Add a teacher" learns
     * the product has a feature they are being denied.
     */
    case "school-teachers":
      return (
        <Shell
          routeId={id}
          session={session}
          navigate={navigate}
          actions={actions}
        >
          <TeachersScreen
            auth={auth}
            lang={lang}
            canWrite={session?.role === "ADMIN"}
          />
        </Shell>
      );

    /*
     * ⚠️ A PRINCIPAL READS IT AND WRITES NOTHING, exactly like E3 above. S6 makes
     * them school read-only and every write on this screen is ADMIN-only in the
     * service; `canWrite` decides whether the forms are RENDERED, which is D4's
     * rule — a principal shown a disabled form learns the product has a feature
     * they are being denied.
     */
    case "school-classes":
      return (
        <Shell
          routeId={id}
          session={session}
          navigate={navigate}
          actions={actions}
        >
          <ClassesScreen
            auth={auth}
            lang={lang}
            canWrite={session?.role === "ADMIN"}
          />
        </Shell>
      );

    /*
     * 🔴 THE ROW-LEVEL READ BEHIND E1's GAME CARDS. Those answer *"is this
     * game being used"*; this answers *"who played it, when, and with whom"* —
     * the question a school is asked at the gate and could not answer without
     * opening one child at a time.
     */
    case "school-games":
      return (
        <Shell
          routeId={id}
          session={session}
          navigate={navigate}
          actions={actions}
        >
          <SchoolGamesScreen auth={auth} lang={lang} game={gameFilter} />
        </Shell>
      );

    /*
     * ⚠️ A READ, AND THE ONLY PART OF F3 A SCHOOL SEES. Every write on it is
     * `@Roles('SUPER_ADMIN')` — a school changing its own plan is the
     * discount-waiting-to-happen §4 rules out for self-reported seats.
     */
    case "school-subscription":
      return (
        <Shell
          routeId={id}
          session={session}
          navigate={navigate}
          actions={actions}
        >
          <OurPlanScreen auth={auth} lang={lang} />
        </Shell>
      );

    case "platform":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <PlatformDashboard
            auth={auth}
            lang={lang}
            onOpenSchools={() => {
              navigate("platform-schools");
            }}
            /* 🔴 THE ENROLMENT TILE'S DRILL-DOWN. It has said "Children
               enrolled: N" since this screen was built and there was nowhere to
               go — see `PlatformChildrenScreen`. */
            onOpenChildren={() => {
              navigate("platform-children");
            }}
            /* ⚠️ A NAVIGATE, NOT A FETCH. Reading the audit log writes a
               `SUPER_ADMIN_ACCESS` row, so the dashboard links to F5 rather
               than pulling rows onto a card that loads itself. */
            onOpenAudit={() => {
              navigate("platform-audit");
            }}
            onOpenParents={() => {
              navigate("platform-parents");
            }}
            /* The ranked-institutions card's "View All". Change 19's engagement
               screen is where per-school engagement is answered in full; without
               this prop the card draws no button rather than a dead one. */
            onOpenEngagement={() => {
              navigate("platform-engagement");
            }}
          />
        </Shell>
      );

    /*
     * Every child on the platform. `chrome: true` in the route table, so it is
     * role-gated and not a sidebar row — it is reached from F1's enrolment tile.
     */
    case "platform-children":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <PlatformChildrenScreen auth={auth} lang={lang} />
        </Shell>
      );

    case "platform-parents":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <PlatformParentsScreen auth={auth} lang={lang} />
        </Shell>
      );

    case "platform-schools":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          {/* X4 — the panel is drawn only because this callback exists. */}
          <SchoolsScreen auth={auth} lang={lang} onActAs={actions.actAs} />
        </Shell>
      );

    /*
     * ✅ F3 — BUILT, AND OD-122 RECORDS WHY IT WAS NOT. Prices are data an
     * operator enters, and nothing here charges anybody: there is still no
     * payment provider, so recording a payment is writing down that money
     * arrived by some other route.
     */
    case "platform-engagement":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <PlatformEngagementScreen auth={auth} lang={lang} />
        </Shell>
      );

    case "platform-subscriptions":
      return (
        <Shell
          routeId={id}
          session={session}
          navigate={navigate}
          actions={actions}
        >
          <SubscriptionsScreen auth={auth} lang={lang} />
        </Shell>
      );

    /* F3b — the discount codes and the calculator that was missing entirely. */
    case "platform-coupons":
      return (
        <Shell
          routeId={id}
          session={session}
          navigate={navigate}
          actions={actions}
        >
          <PlatformCouponsScreen auth={auth} lang={lang} />
        </Shell>
      );

    case "platform-audit":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <AuditLogScreen auth={auth} lang={lang} />
        </Shell>
      );

    /*
     * ✅ G3–G4, the classroom machine's own settings. `useReduceMotion.ts`
     * applies a switch it says lives on G3, and until this work nothing in the
     * product could set it.
     *
     * 🔴 G1 IS GONE — the account tab, its change-password form and its
     * authenticator panel. Nothing here writes any more, which is why neither
     * screen below is handed a token: both read and write browser-local
     * preferences (`useReduceMotion`, `playPreference`) and neither has a server
     * call to authenticate. The rule the removed screen established still holds
     * for whatever lands here next — a settings screen takes a bearer
     * credential, never the session object, or it starts deciding things the
     * server decides.
     *
     * ⚠️ MFA DID NOT LEAVE THE PRODUCT WITH IT. `MfaEnrolmentBody` and
     * `RecoveryCodesPanel` are still the FORCED enrolment an operator is held
     * through at sign-in (`OperatorLoginScreen`, `isMfaEnrolmentChallenge`
     * above). What went is the voluntary "set up an authenticator" entry point.
     */
    case "settings-accessibility":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <AccessibilitySettingsScreen
            lang={lang}
            role={session?.role ?? null}
            navigate={navigate}
          />
        </Shell>
      );

    case "settings-camera":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <CameraSettingsScreen
            lang={lang}
            role={session?.role ?? null}
            navigate={navigate}
          />
        </Shell>
      );

    case "platform-broadcast":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <PlatformBroadcastScreen auth={auth} lang={lang} />
        </Shell>
      );

    /*
     * F6 — the schools waiting for a phone call. Its own route, because these
     * are schools that are not customers yet.
     */
    case "platform-requests":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <RequestsScreen auth={auth} lang={lang} />
        </Shell>
      );

    /* Who can reach every child on this platform — its own screen now. */
    case "platform-operators":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <PlatformOperatorsScreen auth={auth} lang={lang} />
        </Shell>
      );

    case "platform-system":
      return (
        <Shell
          session={session}
          navigate={navigate}
          actions={actions}
          routeId={id}
        >
          <SystemScreen auth={auth} lang={lang} />
        </Shell>
      );

    default:
      /*
       * ⚠️ UNREACHABLE TODAY, AND KEPT RATHER THAN DELETED. Every `RouteId` above
       * has an arm, so this fires only for a route added to the table without a
       * screen — which is precisely the OD-89 failure, and a labelled page is how
       * it stays visible instead of silently falling back to the landing screen.
       */
      return (
        <Placeholder title="Not built yet" note="This route has no screen." />
      );
  }
}

/**
 * 🔴 DRILL-DOWNS ARE LOCAL STATE, NOT ROUTES, AND THAT IS DELIBERATE.
 *
 * `ClassOverviewScreen`, `ParentDashboardScreen` and `SchoolDashboard` each take
 * a callback for opening a child, a detail view or a class's consent. They were
 * written to be composed by a parent that holds the selection — and a router
 * that passed no-op handlers would render buttons that do nothing, which is the
 * exact antipattern D4 forbids one tier up.
 *
 * ⚠️ THEY COULD BE ROUTES, and one day should be: a teacher who bookmarks a
 * child's page expects it to open that child. That needs route PARAMETERS, which
 * this router does not have — and adding them is a real design decision (an id
 * in a URL is a thing a user can edit, so every parameterised route needs its
 * own scoping story). 📋 Reported rather than guessed at.
 */
/*
 * ✅ THE TEACHER TIER IS `TeacherApp` NOW, AND THE VERSION THAT WAS HERE IS THE
 * REASON WHY.
 *
 * It held `const [classId] = useState('')` — correctly refusing to guess between
 * two classes, and stopping one step short of the answer — so every teacher
 * screen issued `GET /classes//overview`, a path Nest has no route for. The tier
 * rendered *"Couldn't load this."* from the first day it was reachable, while
 * `GET /classes` sat built, scoped and uncalled.
 *
 * It also mounted two screens of seven. `ModeSelect`, `RosterScreen`,
 * `LaunchConfirm`, `LeaderboardScreen` and `ReportsScreen` were written, tested
 * and unreachable, and `teacherFlow.ts` — the machine whose whole purpose is
 * that *no path reaches a game without passing the confirm screen* — was
 * imported by nothing but its own test.
 *
 * `TeacherApp` owns the class choice (`classChoice.ts`, tested), drives the
 * machine, and mounts D1–D7.
 */

/**
 * Which screen each of the tier's six routes asks the reducer for.
 *
 * ⚠️ THE ROUTE ASKS; THE REDUCER DECIDES. `parentFlow` refuses to open a
 * per-child screen with no active child, so a parent with nothing linked who
 * types `#/parent/attendance` lands on C1 with the switcher's own "no children"
 * message rather than on a screen that would request
 * `/parent/children/undefined/attendance`.
 */
const SCREEN_FOR_ROUTE: Partial<Record<RouteId, ParentScreen>> = {
  parent: "C1",
  "parent-attendance": "C6",
  "parent-achievements": "C3",
  "parent-announcements": "ANNOUNCEMENTS",
  "parent-messages": "C4",
  /*
   * 🔴 THE SAME `C2` THE BUTTON ON C1 OPENS, AND DELIBERATELY NOT A COPY. C2 is
   * the child's progress in detail plus the printable `ReportSheet` — which is
   * what "Reports" means in this tier — so the nav item is a second door into
   * one screen rather than a second screen saying the same things. A duplicate
   * would be two places to fix the day the report changes.
   *
   * ⚠️ IT IS NOT IN `ACCOUNT_WIDE`, so a parent with no child linked yet lands
   * on C1 instead of an empty report. That is the existing rule for every
   * per-child route here, and a report about nobody is the clearest case for it.
   */
  "parent-reports": "C2",
};

function ParentTier({
  routeId,
  navigate,
  auth,
  lang,
}: {
  routeId: RouteId;
  navigate: (id: RouteId) => void;
  auth: { token: string };
  lang: Lang;
}) {
  /*
   * ✅ THE SWITCHER RUNS NOW, AND WHAT WAS HERE IS WHY IT HAD TO.
   *
   * This held `const [playerId] = useState('')` under a comment refusing to
   * guess between two children — correct about the guess, and one step short of
   * the answer, so every render issued `GET /parent/children//dashboard`: a path
   * Nest has no route for, answering 404, which C1 reported faithfully as a load
   * failure. It is `TeacherApp`'s `classId = ''` bug one tier over, and
   * `ChildSwitcher` — which fetches the list, hands the ids up and is the reason
   * `parentFlow` can promise the active child is always one of this parent's own
   * — was built, tested and mounted nowhere.
   *
   * 🔴 THE REDUCER OWNS THE CHOICE, NOT THIS COMPONENT. `parentFlow` refuses a
   * `SWITCH_CHILD` to an id outside the list the SERVER returned — the one
   * transition that could put another parent's child in a request path — and
   * keeps the active child across a refetch so a parent reading the second
   * one's progress is not moved back to the first.
   *
   * ✅ C2–C5 AND THE CONSENT SCREEN ARE MOUNTED. They were built, tested and
   * rendered NOWHERE: this component passed `onOpenDetail` and `onOpenConsent`
   * as empty arrow functions, so the two buttons on a parent's dashboard did
   * nothing when tapped. Five working screens sat behind them.
   */
  const [flow, dispatch] = useReducer(parentFlow, undefined, initialParentFlow);

  const childId = flow.activeChildId;

  /*
   * 🔴 THE ROUTE NAMES THE SECTION; THE REDUCER OWNS THE ONE LEAF THAT HAS NO
   * ROUTE.
   *
   * Five of the six sections are routes, so the bottom bar can link to them and
   * a parent can bookmark one. C2 — progress detail — is deliberately not a
   * route (`routes.ts` explains the item budget), so it is the reducer's, opened
   * from C1's button and closed by BACK. Reading the route FIRST and deferring
   * to the reducer only for that leaf means a nav tap always wins: the failure
   * mode of the other order is a parent stuck on C2 because tapping "Progress"
   * changed a route that was already `parent`.
   */
  const routeScreen = SCREEN_FOR_ROUTE[routeId] ?? "C1";
  const requested =
    routeId === "parent" && flow.screen === "C2" ? "C2" : routeScreen;

  /*
   * 🔴 AND THE REDUCER'S GUARD IS RE-APPLIED HERE, because a URL is typed rather
   * than tapped. `#/parent/attendance` on an account with nothing linked would
   * otherwise request `/parent/children/undefined/attendance`; C1 plus the
   * switcher's own "no children linked" message is the honest answer.
   */
  const screen: ParentScreen =
    childId === null && !ACCOUNT_WIDE.includes(requested) ? "C1" : requested;

  return (
    <>
      {/*
        ⚠️ THE SWITCHER IS ABOVE EVERY SECTION, NOT ONLY C1. A parent of two who
        switched child on Progress and then opened Attendance would otherwise be
        reading the second child's absences under a control they cannot see —
        and `ChildSwitcher` draws itself only when there is more than one child,
        so a one-child account costs nothing for it.
      */}
      <ChildSwitcher
        activeChildId={childId}
        auth={auth}
        lang={lang}
        onSwitch={(playerId) => {
          dispatch({ type: "SWITCH_CHILD", playerId });
        }}
        onChildrenLoaded={(childIds) => {
          dispatch({ type: "CHILDREN_LOADED", childIds });
        }}
      />

      {/*
        ⚠️ NOTHING PER-CHILD IS RENDERED UNTIL A CHILD IS KNOWN. `ChildSwitcher`
        draws the loading, error and "no children linked" states for the account
        itself; mounting a screen with an empty id underneath it is exactly the
        404 this component used to produce.

        The three the reducer names in `ACCOUNT_WIDE` are the exception: they are
        about the ACCOUNT rather than a child. C5 is the only way a parent with
        nothing linked can turn messaging off, and the announcements board is the
        one screen that works for exactly the parent whose roster linkage is still
        being set up — being told the school is shut on Friday does not require
        the school to have linked their child first.
      */}
      {childId === null ? (
        screen === "C4" ? (
          <NotificationsScreen auth={auth} lang={lang} />
        ) : screen === "ANNOUNCEMENTS" ? (
          <AnnouncementsScreen auth={auth} lang={lang} />
        ) : screen === "CONSENT" ? (
          /* ✅ THE PREFERENCES ALONE FOR A PARENT WITH NOTHING LINKED YET
             (ADR-051). The consent half needs a child; the language, the quiet
             hours and what we may send do not, and this family is precisely the
             one still being set up. */
          <SettingsScreen
            playerId={null}
            auth={auth}
            lang={lang}
            /* Nothing to scroll to — there is no consent section without a
               child — so the button is inert rather than moving the page. */
            onOpenConsent={() => {
              /* no consent section on this variant */
            }}
          />
        ) : null
      ) : (
        <ParentSection
          screen={screen}
          playerId={childId}
          auth={auth}
          lang={lang}
          onOpenDetail={() => {
            dispatch({ type: "OPEN", screen: "C2" });
          }}
          onBack={() => {
            dispatch({ type: "BACK" });
          }}
          onOpenConsent={() => {
            navigate("parent");
          }}
          /* Same rule, same reason — the notice board is a route. */
          onOpenAnnouncements={() => {
            navigate("parent-announcements");
          }}
          /* ✅ AND SO IS THE TEACHER CONVERSATION. This used to be the line
             above, handed to the messages panel as well — so "View All" and
             "Contact Teacher" both opened Notices. */
          onOpenMessages={() => {
            navigate("parent-messages");
          }}
          /* ✅ THE OVERVIEW'S TWO NEW PANELS OPEN THE SCREENS THEY SUMMARISE.
             Both are routes rather than reducer states, for the reason
             `onOpenConsent` records: a panel that changed the display without
             changing the URL would leave the sidebar highlighting Overview. */
          onOpenAttendance={() => {
            navigate("parent-attendance");
          }}
          onOpenAchievements={() => {
            navigate("parent-achievements");
          }}
        />
      )}
    </>
  );
}

/** One child is known; this picks the screen. Split out so the null case above stays readable. */
function ParentSection({
  screen,
  playerId,
  auth,
  lang,
  onOpenDetail,
  onOpenConsent,
  onOpenAnnouncements,
  onOpenMessages,
  onOpenAttendance,
  onOpenAchievements,
  onBack,
}: {
  screen: ParentScreen;
  playerId: string;
  auth: { token: string };
  lang: Lang;
  onOpenAnnouncements: () => void;
  /**
   * 🔴 THE MESSAGES ROUTE, AND IT IS NOT `onOpenAnnouncements`. The dashboard's
   * "Messages from Teacher" panel was handed the announcements navigator, so
   * both of its buttons — "View All" and "Contact Teacher" — opened the Notices
   * board. A parent tapping "Contact Teacher" landed on a page with no teacher
   * on it and no way to write to one.
   *
   * ⚠️ ITS OWN PROP RATHER THAN A REUSED ONE, WHICH IS THE POINT. Two
   * destinations sharing one callback is what let this happen silently: the
   * buttons worked, they navigated, and nothing anywhere was undefined.
   */
  onOpenMessages: () => void;
  /** The Attendance screen, from the overview's week strip. */
  onOpenAttendance: () => void;
  /** The Achievements screen, from the overview's rewards panel. */
  onOpenAchievements: () => void;
  onOpenDetail: () => void;
  onOpenConsent: () => void;
  onBack: () => void;
}) {
  /*
   * 🔴 `key={playerId}` ON EVERY PER-CHILD SCREEN. Each fetches in a `useResource`
   * keyed on its props; without a remount, switching child on a screen that is
   * already loaded shows the previous child's numbers under the new child's name
   * for as long as the request takes. On this tier that is somebody's child.
   */
  switch (screen) {
    case "C2":
      /*
       * 🔴 THE ONLY SCREEN THAT NEEDS A BACK CONTROL, because it is the only one
       * the bottom bar cannot reach. Without it a parent who tapped "Progress
       * detail" has no way out that does not involve the browser's own back
       * button — and this tier is read inside a WhatsApp in-app browser as often
       * as a real one.
       */
      return (
        <>
          {/* ⚠️ `print:hidden` — THIS SCREEN IS THE ONE THAT PRINTS. `ReportSheet`
              is the document; a back chevron above it is a control on a page
              nobody can press, and it is the shape that makes a print-to-PDF look
              like a screenshot. `print.css` removes the shell's own chrome; this
              is the one piece of chrome the shell does not own. */}
          <div className="mb-4 print:hidden">
            <BackButton onClick={onBack} label={PARENT_COPY[lang].dashboard} />
          </div>
          <ProgressDetailScreen
            key={playerId}
            playerId={playerId}
            auth={auth}
            lang={lang}
          />
        </>
      );
    case "C6":
      return (
        <AttendanceScreen
          key={playerId}
          playerId={playerId}
          auth={auth}
          lang={lang}
        />
      );
    case "C3":
      return (
        <AchievementsScreen
          key={playerId}
          playerId={playerId}
          auth={auth}
          lang={lang}
        />
      );
    /*
     * 🔴 THE CONVERSATION FIRST, THE DELIVERY LOG SECOND (ADR-052).
     *
     * This screen is called "Messages" in the parent's sidebar and it drew C4
     * alone — which is the NOTIFICATION log: which weekly reports went out, by
     * WhatsApp or in-app. So a teacher could send a message from her own
     * Messages screen, and the parent could open a screen with the same name and
     * be told *"No messages yet"* while it sat unread in a thread two levels
     * down, behind a button on the progress DETAIL screen.
     *
     * Two different things were called Messages. The one a parent means when
     * they tap the word is the one the teacher typed, so it is drawn first and
     * the delivery log keeps its place underneath.
     *
     * ⚠️ THE THREAD IS GUARDED ON A CHILD AND THE LOG IS NOT. C4 is
     * `ACCOUNT_WIDE` — a parent whose child is not linked yet still has a
     * delivery log and still needs to see that nothing has been sent. A thread
     * needs a child to be a conversation with.
     */
    case "C4":
      return (
        <>
          {playerId !== null && (
            <div className="mb-8">
              <TeacherThread
                key={playerId}
                playerId={playerId}
                auth={auth}
                lang={lang}
              />
            </div>
          )}
          {/*
            ✅ "What we have sent you" IS GONE FROM THIS SCREEN, ON REQUEST.
            C4's delivery record used to sit under the teacher thread, so the
            Messages screen was two different inboxes stacked: what a teacher
            wrote, and a log of what the product itself had sent by WhatsApp.
            For most families the second is empty — "Nothing sent yet. Weekly
            reports start once messaging is switched on for your number." — so
            the screen ended on a null state under a real conversation.

            ⚠️ THE COMPONENT AND ITS OTHER RENDER STAY. `NotificationsScreen` is
            still what a parent with no child linked yet gets for this route
            (see the `childId === null` branch above); removing that too would
            route them to an empty screen, which is the failure this tier keeps
            hitting. 📋 Say so if the whole feature should go — that means
            dropping C4 from `ACCOUNT_WIDE` and from the nav as well.
          */}
        </>
      );
    /* ⚠️ NO `key={playerId}`. Every screen above remounts on a child switch
       because each one is about that child; a notice is about the school and
       every family gets the same one, so remounting it would refetch the same
       list and blank the board for no reason. */
    case "ANNOUNCEMENTS":
      return <AnnouncementsScreen auth={auth} lang={lang} />;

    /*
     * ⚠️ A LEAF LIKE C2, REACHED FROM ITS BUTTON AND CLOSED BY BACK — and it is
     * deliberately not a route. `routes.ts` budgets this tier at six nav items
     * and they are spent; a conversation is opened from the screen that
     * prompted it, which is where a parent is when they think of the question.
     */
    case "THREAD":
      return (
        <>
          <div className="mb-4 print:hidden">
            <BackButton onClick={onBack} label={PARENT_COPY[lang].dashboard} />
          </div>
          <TeacherThread
            key={playerId}
            playerId={playerId}
            auth={auth}
            lang={lang}
          />
        </>
      );
    /*
     * ✅ PRIVACY IS NOW THE PARENT'S ONLY SETTINGS PAGE (ADR-051), so the two
     * halves are drawn together: the consent decision, then the preferences that
     * act on it.
     *
     * 🔴 THE RECORDING SWITCH IS WHY THIS IS A MOVE AND NOT A DELETION. I5 makes
     * the parent one layer of a precedence chain that *"any layer may restrict"*
     * — so the tier's Settings control could go, but the control behind it could
     * not, or a parent would keep a right they had no way to exercise. Privacy
     * already carried export and erasure, which `routes.ts` calls *"rights
     * rather than preferences"*; the switch belongs beside them.
     *
     * ⚠️ CONSENT IS PER-CHILD AND THE PREFERENCES ARE HALF ACCOUNT-WIDE, which
     * is why only the first half is guarded on a child. A parent whose children
     * are not linked yet still has a language and a quiet-hours window, and the
     * page must not be empty for them.
     */
    case "CONSENT":
      return (
        <>
          {playerId !== null && (
            <div id="parent-consent" className="mb-8">
              <ConsentScreen
                key={playerId}
                playerId={playerId}
                auth={auth}
                lang={lang}
              />
            </div>
          )}
          <SettingsScreen
            key={playerId}
            playerId={playerId}
            auth={auth}
            lang={lang}
            /* Same page now, so the button scrolls rather than navigates. It is
               kept rather than removed because the sentence that offers it is
               the one that tells a parent consent is theirs to change. */
            onOpenConsent={() => {
              document
                .getElementById("parent-consent")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          />
        </>
      );
    default:
      return (
        <ParentDashboardScreen
          key={playerId}
          playerId={playerId}
          auth={auth}
          lang={lang}
          /* ✅ BOTH OF THESE WERE EMPTY ARROW FUNCTIONS, so the two buttons on
             the dashboard did nothing at all when a parent tapped them. */
          onOpenDetail={onOpenDetail}
          onOpenConsent={onOpenConsent}
          onOpenAnnouncements={onOpenAnnouncements}
          onOpenMessages={onOpenMessages}
          onOpenAttendance={onOpenAttendance}
          onOpenAchievements={onOpenAchievements}
        />
      );
  }
}

/**
 * Everywhere E1 can send a reader — its quick-action bar AND its stat strip —
 * resolved to the routes that already exist.
 *
 * 🔴 THE BAR SHIPPED WITH SIX BUTTONS AND NO HANDLERS. Two of them named
 * features this product does not have and are deleted; these are the ones
 * with somewhere to go. The mapping lives HERE rather than inside the dashboard
 * because navigation is the router's job — `SchoolDashboard` is also rendered by
 * the operator tier's `SchoolDashboardView`, which is looking at somebody else's
 * school and must not be given "Add a teacher" at all. It simply does not pass
 * the prop, and the bar is not drawn.
 *
 * 🔴 `reports` IS THE LAST ROW AND HAS NO BUTTON ON THE BAR. Two tiles land on
 * it — "Played this week" and "Sessions this week" — and E5 is the screen that
 * prints both figures with the classes behind them. The bar names writes, and
 * there is nothing to create on a reports screen.
 *
 * ⚠️ EVERY DESTINATION HERE ADMITS BOTH `ADMIN` AND `PRINCIPAL`, so no tile
 * and no button is a door a principal cannot walk through. `navigate` still runs
 * the same `mayNavigate` check the sidebar does, so a row that later narrows to
 * `ADMIN` lands nowhere rather than rendering a screen they may not have.
 */
const SCHOOL_SECTION_ROUTE = {
  classes: "school-classes",
  teachers: "school-teachers",
  students: "school-students",
  reports: "school-reports",
} as const satisfies Record<string, RouteId>;

function SchoolTier({
  auth,
  lang,
  navigate,
  openGames,
}: {
  auth: { token: string };
  lang: Lang;
  navigate: (id: RouteId) => void;
  /** A game card's destination — the log, seeded with that game. */
  openGames: (game?: string) => void;
}) {
  const [consentClassId, setConsentClassId] = useState<string | null>(null);

  if (consentClassId !== null) {
    return (
      <div>
        <button
          type="button"
          onClick={() => {
            setConsentClassId(null);
          }}
          className="mb-4 rounded-lg px-3 py-2 text-dash-sm text-ink-soft hover:bg-canvas-sunk"
        >
          Back to the school dashboard
        </button>
        <ConsentDashboard classId={consentClassId} auth={auth} lang={lang} />
      </div>
    );
  }
  return (
    <SchoolDashboard
      auth={auth}
      lang={lang}
      onOpenClassConsent={setConsentClassId}
      /* ⚠️ `navigate`, NOT `window.location.hash`. The router owns the hash and
         `mayNavigate` is applied on the way through it; a screen writing the
         hash itself would be the one path around that check. */
      onNavigate={(to) => {
        navigate(SCHOOL_SECTION_ROUTE[to]);
      }}
      /* ⚠️ THE GAME GOES WITH THE CLICK. Opening the log on "all games" from a
         card the reader picked deliberately would make them re-choose what they
         just chose. */
      onOpenGame={openGames}
    />
  );
}

/**
 * E7 and its one leaf.
 *
 * 🔴 THE HUB HOLDS THE OPEN CHILD, AND THE ROW TRAVELS RATHER THAN AN ID. E7 has
 * already fetched every child in the school in one request; handing the leaf a
 * `playerId` would make it fetch all of them again to render a name and a class.
 * Every figure on the report still comes from the leaf's own five calls.
 *
 * ⚠️ THE LIST IS UNMOUNTED WHILE THE REPORT IS OPEN, WHICH IS THE OPPOSITE OF
 * THE PARENT TIER'S RULE AND CORRECT HERE. `ParentTier` stays mounted across six
 * routes because it holds the active child; this is one route with a leaf, and
 * coming back should show today's roster rather than the one loaded before a
 * teacher moved a child.
 */
function SchoolStudentsTier({
  auth,
  lang,
  canWrite,
}: {
  auth: { token: string };
  lang: Lang;
  /** E7 — whether the enrol form is drawn. S6: a principal reads only. */
  canWrite: boolean;
}) {
  const [child, setChild] = useState<StudentRow | null>(null);

  if (child !== null) {
    return (
      <SchoolStudentReport
        /* A different child is a different page — remounting drops the previous
           child's five resources rather than showing them under a new name. */
        key={child.playerId}
        student={child}
        auth={auth}
        lang={lang}
        /*
         * 🔴 THE SAME `role === 'ADMIN'` E7's ENROL FORM USES, AND IT GATES A
         * DIFFERENT WRITE FOR A DIFFERENT REASON. S6 makes a principal school
         * read-only, which is why they get no enrol form; `messages.service.ts`
         * refuses them a family's thread because *"oversight is not membership
         * of somebody else's correspondence"*. Two rules, one predicate — the
         * server enforces both regardless.
         */
        canMessage={canWrite}
        onBack={() => {
          setChild(null);
        }}
      />
    );
  }

  return (
    <SchoolStudentsScreen
      auth={auth}
      lang={lang}
      canWrite={canWrite}
      onOpenChild={setChild}
    />
  );
}

/** The chrome's own controls — quiet, warm, and never the page's colour. */
const CHROME_BUTTON =
  "inline-flex h-9 items-center gap-2 rounded-pill px-3.5 font-dash text-dash-sm font-bold text-ink-soft transition-colors duration-150 ease-dash hover:bg-canvas hover:text-ink";

/**
 * ✅ `Shell` NO LONGER TAKES A `title`, AND FIFTEEN CALL SITES LOST AN ARGUMENT.
 *
 * The chrome used to print the route's name above every screen. It was the third
 * place the same word appeared — the sidebar highlights the current item, and
 * each screen's own `PageHead` states it again with the sentence that explains
 * what the screen is *for* — and it was a second `<h1>` on every page.
 * `DashboardShell` records the full reasoning.
 *
 * ⚠️ `routeId` STAYS AND IS STILL LOAD-BEARING: it drives the sidebar's
 * `active`/`aria-current` state, which is now the *only* thing telling a person
 * where they are. `RouteDef.title` also stays — the nav labels come from it.
 */
function Shell({
  routeId,
  session,
  navigate,
  actions,
  nav = "sidebar",
  contentMaxWidthClass,
  children,
}: {
  routeId: RouteId;
  session: Session | null;
  navigate: (id: RouteId) => void;
  actions: SessionActions;
  nav?: DashboardNav;
  contentMaxWidthClass?: string;
  children: React.ReactNode;
}) {
  /*
   * The chrome follows the language switch like everything else — `main.tsx`
   * wraps the app in `I18nProvider`, so this is available on every screen.
   */
  const { lang } = useLang();
  const t = PLATFORM_COPY[lang];

  const role = session?.role ?? null;

  /*
   * ⚠️ THE HOOK IS CALLED UNCONDITIONALLY AND SWITCHED BY ITS ARGUMENT, because
   * hooks cannot sit behind an `if`. A staff shell passes `false`, and the hook
   * then opens no socket and issues no request.
   */
  const parentUnread = useUnreadMessages(session?.token, role === "PARENT");

  /*
   * 🔴 AN OPERATOR'S DUTY DECIDES WHICH SIDEBAR ROWS CAN LOAD, AND WHAT THE
   * BADGE UNDER THE WORDMARK SAYS. Fetched for operators only — every other tier
   * passes no token, and the hook then issues no request.
   */
  const access = useAccess(role === "SUPER_ADMIN" ? session?.token : undefined);
  const navRoutes = useMemo(
    () => navFor(role).filter((r) => navPermitted(r.id, (p) => access.can(p))),
    [role, access],
  );

  /*
   * ✅ THE SIDEBAR'S COUNT PILLS (MASTER-PLAN §3 F5) — what is waiting behind
   * Pipeline, Renewals, Support and Messages, from `GET /me/nav-badges`.
   *
   * ⚠️ ASKED ONLY WHEN THIS TIER DRAWS A BADGED ROW OTHER THAN THE PARENT'S
   * MESSAGES, which keeps its live, socket-driven count from
   * `useUnreadMessages` above — a 60-second poll would be a step backwards
   * there. A tier with no badged row issues no request at all.
   */
  const wantsBadges = navRoutes.some((r) => NAV_BADGE[r.id] !== undefined && r.id !== "parent-messages");
  const navBadges = useNavBadges(session?.token, wantsBadges);
  const badgeFor = (id: RouteId): number | undefined => {
    if (id === "parent-messages") return parentUnread;
    const key = NAV_BADGE[id];
    return key === undefined ? undefined : badgeCount(navBadges, key);
  };

  /*
   * ✅ ONE SETTINGS CONTROL PER TIER, RESOLVED ONCE AND DRAWN TWICE. A parent
   * gets their own screen (`parent/settings`); every other adult gets G1. The
   * decision lives in `routes.ts` beside the table it reads — see
   * `settingsHomeFor`, which records why a parent has no password to change.
   */
  const settingsRoute = settingsHomeFor(role);
  const inSettings = isSettingsRoute(routeId);

  /*
   * ✅ P2-06 (for PARENT) — PRIVACY IS A SUB-VIEW OF HOME (`#/parent?view=privacy`,
   * ADR-051), SO THE CHROME HAS TO READ THE QUERY TO SAY WHERE YOU ARE. Without
   * it the thumb bar lit "Home" while the Privacy sheet was open, and the
   * desktop sidebar had no Privacy row at all — the "one tap" ADR-051 promises
   * was a tap into "More" on a phone and a hunt on a laptop.
   */
  const [hashView, setHashView] = useState<string | null>(() =>
    hashParam(window.location.hash, "view"),
  );
  useEffect(() => {
    const onHash = (): void => {
      setHashView(hashParam(window.location.hash, "view"));
    };
    window.addEventListener("hashchange", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
    };
  }, []);
  const parentPrivacyOpen =
    role === "PARENT" && routeId === "parent" && hashView === "privacy";
  const privacyHref = `${pathFor("parent")}?view=privacy`;

  /*
   * ✅ THE SEARCH BOX REACHES A SCHOOL'S OWN RECORDS, NOT JUST ITS SCREENS.
   *
   * 🔴 LOADED ON FIRST FOCUS, NEVER ON RENDER. This is three whole-collection
   * reads; doing them because a screen mounted would put three requests behind
   * every navigation in the tier, for a box most visits never touch. `onActivate`
   * fires once, the first time somebody actually clicks into it.
   *
   * 🔴 ADMIN AND PRINCIPAL ONLY, AND THAT IS NOT A GUESS — it is the `roles` list
   * on the three routes these results navigate to. A TEACHER has no
   * `school/students` screen, so indexing children for one would build a result
   * whose only destination answers 403. The parent and platform tiers stay
   * destinations-only for the reasons in `search.ts`.
   *
   * ⚠️ A FAILED READ IS AN EMPTY LIST, NOT A BROKEN BOX. If the roster call
   * fails the box still searches destinations, which is exactly what it did
   * before this existed — a degraded search beats a search that throws.
   */
  const [corpus, setCorpus] = useState<SearchItem[]>([]);
  const [wantCorpus, setWantCorpus] = useState(false);
  const canIndexRecords = role === "ADMIN" || role === "PRINCIPAL";
  const token = session?.token;

  useEffect(() => {
    if (!wantCorpus || !canIndexRecords) return;
    const ac = new AbortController();
    const auth = { token, signal: ac.signal };
    void Promise.all([
      getAllStudents(auth).catch(() => []),
      getClasses(auth).catch(() => []),
      getSchoolStaff(auth).catch(() => []),
    ]).then(([students, classes, staff]) => {
      if (!ac.signal.aborted) {
        setCorpus(schoolSearchItems(students, classes, staff));
      }
    });
    return () => {
      ac.abort();
    };
  }, [wantCorpus, canIndexRecords, token]);

  /*
   * What the chrome bar calls the screen underneath it.
   *
   * 🔴 IT WAS THE WORD "Dashboard", HARDCODED IN `DashboardShell`, ON EVERY
   * SCREEN THIS FUNCTION RENDERS. Removing the old `title` prop was right and
   * the literal left behind it was not: a head teacher on Children, Teachers,
   * Classes or Our plan was told at the top of the window that they were on the
   * dashboard, while the sidebar highlighted the row they had actually clicked.
   *
   * ⚠️ DERIVED FROM `routeId`, WHICH THIS COMPONENT ALREADY HAD FOR THE
   * SIDEBAR'S `aria-current`. One id, one word, drawn twice — the bar cannot
   * disagree with the highlighted row, because neither is written by hand.
   *
   * ⚠️ AND IT IS `pageTitleFor`, NOT `navLabel`, FOR ONE ROUTE'S SAKE. The
   * school tier's home is the row "School" in a list of nouns and the page
   * "Dashboard" above the tiles; `nav.tsx` holds both words and this reads the
   * second. Everything else falls through to the sidebar's own label.
   */
  const pageLabel = pageTitleFor(routeId, lang, titleFor(routeId));

  /*
   * What the chrome's search box may offer: this role's own nav, plus the one
   * chrome destination that is a screen — Settings.
   *
   * ⚠️ THE LABELS ARE `navLabel`'s, NOT `RouteDef.title`. The sidebar is
   * translated and the search results are the same words; a box that answered in
   * English over a Hindi sidebar would be a second vocabulary for one product.
   *
   * ⚠️ MEMOISED ON THE ROLE AND THE LANGUAGE. `GlobalSearch` memoises its
   * ranking on this array's identity, and a fresh array every render would
   * re-rank on every keystroke elsewhere in the shell.
   */
  const searchable = useMemo<SearchItem[]>(() => {
    const rows: SearchItem[] = navRoutes.map((r) => ({
      id: r.id,
      label: navLabel(r.id, lang, r.title),
      hint: `#/${r.path}`,
      keywords: r.keywords,
    }));
    if (settingsRoute) {
      rows.push({
        id: settingsRoute,
        label: t.settings,
        hint: `#/${pathFor(settingsRoute).replace(/^#\//, "")}`,
        keywords: ["preferences", "account", "language", "privacy"],
      });
    }
    /*
     * ⚠️ RECORDS AFTER DESTINATIONS, WHICH IS THE TIE-BREAK ORDER TOO. `searchItems`
     * breaks an exact tie on kind first and then on this array's order, so the two
     * agree rather than one quietly undoing the other.
     */
    return [...rows, ...corpus];
  }, [navRoutes, lang, settingsRoute, t.settings, corpus]);

  /*
   * One row of this tier's navigation, drawn by the sidebar, the drawer and the
   * thumb bar alike — one definition, so the three cannot disagree about a
   * badge or about which row is current.
   */
  const navRow = (r: (typeof navRoutes)[number]) => (
    <NavItem
      key={r.id}
      href={pathFor(r.id)}
      icon={navIcon(r.id)}
      tint={navTint(r.id)}
      /*
       * 🔴 ONLY THE ROWS THAT CAN BE WAITING ON YOU — `NAV_BADGE` in nav.tsx:
       * Pipeline, Renewals, Support and Messages. A parent once had no way to
       * learn a message had arrived except by opening the Messages screen and
       * looking; the operator and teacher tiers had the same gap for new
       * enquiries, renewals due and tickets.
       *
       * ⚠️ NEVER EVERY ROW "FOR CONSISTENCY". A number on Overview or Reports
       * that nothing on the server backs is the invented figure this codebase
       * spends its comments refusing. A count of 0 draws nothing.
       */
      badge={badgeFor(r.id)}
      badgeLabel={(n) => (NAV_BADGE[r.id] === "messages" ? t.unreadMessages(n) : `${String(n)} new`)}
      /*
       * ✅ THE SIDEBAR SHOWS WHERE YOU ARE, WHICH IT DID NOT. Six identical
       * text links with no current-page state means the only way to know
       * which screen is open is to read the heading — and on the platform
       * tier "Schools", "Content" and "System" all open pages that begin
       * with a table. `aria-current="page"` rides along with the fill, so
       * the answer reaches a screen reader too.
       */
      active={r.id === routeId && !(parentPrivacyOpen && r.id === "parent")}
      onClick={() => {
        navigate(r.id);
      }}
    >
      {navLabel(r.id, lang, r.title)}
    </NavItem>
  );

  /* P2-06 — the parent's Privacy row, drawn in the sidebar and in "More". */
  const privacyRow =
    role === "PARENT" ? (
      <NavItem
        key="parent-privacy-view"
        href={privacyHref}
        icon={<IconShield />}
        active={parentPrivacyOpen}
        onClick={() => {
          window.location.hash = privacyHref;
        }}
      >
        {t.privacy}
      </NavItem>
    ) : null;

  /*
   * ✅ THE PARENT'S THUMB BAR IS FOUR DESTINATIONS AND "MORE", NOT SIX.
   *
   * 🔴 SIX CELLS ACROSS A PHONE CUT TWO OF THEM TO "Achievem…" AND "Announce…",
   * AND THE BAR HAD NO SIGN-OUT. The header `actions` below are not drawn by
   * `DashboardShell`, and the sidebar footer is `md` and up, so a parent on a
   * phone could not sign out at all. The first four are the ones a parent opens
   * every visit; the sheet holds the rest, Privacy & consent and Sign out.
   *
   * ⚠️ `adaptive` ONLY. The sidebar and the drawer still list every route.
   */
  const thumbSlots = 4;
  /* In the sidebar's own order — `navSections`, not the route table's. */
  const thumbOrder =
    nav === "adaptive" ? navSections(role, navRoutes, lang).flatMap((section) => section.items) : [];
  const thumbOverflow = thumbOrder.slice(thumbSlots);

  return (
    <DashboardShell
      nav={nav}
      contentMaxWidthClass={contentMaxWidthClass}
      pageLabel={pageLabel}
      brand={
        <div className="flex items-center gap-2.5">
          {/* The console mark (Figma: Components › Logo) — ink tile, white K. */}
          <span
            aria-hidden="true"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] bg-gradient-to-b from-primary-deep to-primary text-[14px] font-semibold text-primary-ink"
          >
            K
          </span>
          {/*
            🔴 THE WORDMARK ONLY. There was a second line here reading "SUPER
            ADMIN", hardcoded, and it drew on every adult screen in the product —
            so a parent opening their child's progress was told they were the
            platform operator, with the real label ("Parent") stacked underneath
            it. Two tier labels, and the louder one wrong.

            ⚠️ THE ANSWER IS THE `tier` PROP BELOW, WHICH ALREADY EXISTED AND WAS
            ALREADY CORRECT. `DashboardShell` documents it as the answer to
            *"whose account am I in"*, `tierLabel` derives it from the session
            role, and it renders directly under this lockup. Deriving the same
            fact twice is what let one copy drift; there is now one copy.
          */}
          <span className="font-dash text-[14.5px] leading-none font-semibold tracking-[-0.01em] text-ink">
            Kalqy
          </span>
        </div>
      }
      tier={tierLabel(role, lang, access.operatorRole)}
      /*
       * 🔴 ONE CONTROL, EVERY ADULT TIER, AND NO ROLE PASSED TO IT. The parent,
       * the teacher and the operator all get the bell from this one line, and
       * what each of them sees inside it is decided server-side from the token
       * — `GET /notices/mine` and `GET /broadcasts/mine`. A `role` prop here
       * would be a second copy of that decision, in the one place a client is
       * least able to make it.
       *
       * ⚠️ `session?.token`, SO IT DRAWS NOTHING WHEN SIGNED OUT. The shell also
       * renders behind the sign-in doors; a bell there would be a control that
       * is permanently empty.
       */
      notifications={<NotificationBell token={session?.token} />}
      /*
       * ✅ THE SEARCH BOX DOES SOMETHING NOW, AND WHAT IT DOES IS ROLE-SCOPED BY
       * CONSTRUCTION.
       *
       * 🔴 IT WAS AN `<input>` DRAWN INSIDE `DashboardShell` WITH NO HANDLER —
       * on every adult screen in the product, reading "Search anything...". The
       * shell's own note about the hamburger applies word for word: *"a control
       * that looks like the way out and is not is worse than no control: the
       * reader stops looking for the real one."*
       *
       * 🔴 IT SEARCHES `navFor(role)` AND NOTHING ELSE, WHICH IS THE HONEST
       * SCOPE. No endpoint in this product takes a query, so a box that
       * appeared to search children or messages would answer "no results" for a
       * child who exists — worse than no box. `search.ts` records the full
       * argument; each screen's own filters still search the data it holds.
       *
       * ⚠️ AND IT GRANTS NOTHING. `navFor` already filters on `roles`, so a
       * teacher typing "schools" is told there is nothing rather than being
       * offered a door that answers 403 — and `RouteDef.roles` is still a
       * courtesy rather than the control, which is the server's.
       */
      search={
        searchable.length === 0 ? null : (
          <GlobalSearch
            items={searchable}
            onActivate={() => {
              setWantCorpus(true);
            }}
            onPick={(hit) => {
              /*
               * ⚠️ A RECORD CARRIES ITS OWN ADDRESS; A DESTINATION IS ITS ID.
               * `navigate` takes a `RouteId` and there is no route id for one
               * child — the href is a screen plus a seeded `?q=`, which
               * `normalisePath` now drops before matching and
               * `canonicalHashFor` therefore leaves intact.
               */
              if (hit.href) {
                window.location.hash = hit.href;
                return;
              }
              navigate(hit.id as RouteId);
            }}
          />
        )
      }
      /*
       * 🔴 DECORATION, AND A DIFFERENT PIECE OF IT PER TIER — OR NONE.
       *
       * ⚠️ THIS USED TO SAY THE SCHOOL TIER "FILLS ITS COLUMN" AND SHOULD HAVE
       * NOTHING. It does not: six items leave the same ~200px of empty cream the
       * parent tier had, which on a first look reads as a list that failed to
       * finish loading rather than as generous spacing.
       *
       * 🔴 WHAT CHANGED IS THE ART, NOT THE RULE. The objection recorded here was
       * to dropping a *character* into a head teacher's console — a mascot they
       * do not know, with none of the parent tier's justification. That still
       * stands, and it is why the school gets a BUILDING instead: the tier that
       * is about one, drawn as one. It answers "whose console is this" on every
       * school screen, which matters most for an operator who has just
       * impersonated into a school.
       *
       * ⚠️ WHICH BUILDING IS `play.css`'s CALL, NOT THIS FILE'S, and it is the
       * schoolhouse — flag, clock, the word on the board — rather than the
       * generic village house that stood in for one first. See `.play-house`.
       *
       * ⚠️ THE PLATFORM TIER GETS NOTHING, AND THAT IS THE SAME REASONING RATHER
       * THAN AN EXCEPTION TO IT. Seven items already fill that column — the
       * shell's own note warns a seventh crowds sign-out off a 768px window — so
       * there is no dead space to fill and nothing to justify filling it with.
       */
      /*
       * ⚠️ NO SIDEBAR ART IN THE CONSOLE. The redesign (Figma "Kalqy", Admin
       * page) draws every adult sidebar as nav + account and nothing else; the
       * sidekick and the schoolhouse belonged to the warm theme.
       */
      sidebarArt={null}
      /*
       * Sections and headings come from `navSections` (the Figma sidebar
       * layout); which routes appear at all is `navFor(role)` narrowed by the
       * operator's duty (`navPermitted`).
       */
      navItems={
        <>
          {navSections(role, navRoutes, lang).map((section, si) => (
            <Fragment key={section.title ?? `section-${String(si)}`}>
              {section.title ? <NavGroup>{section.title}</NavGroup> : null}
              {section.items.map(navRow)}
            </Fragment>
          ))}
          {privacyRow}
        </>
      }
      thumbItems={
        nav === "adaptive" ? thumbOrder.slice(0, thumbSlots).map(navRow) : undefined
      }
      thumbMore={
        nav === "adaptive"
          ? {
              label: t.more,
              icon: <IconMore />,
              /* P2-06 — Privacy lives in this sheet, so the sheet is "where you are". */
              active:
                thumbOverflow.some((r) => r.id === routeId) || parentPrivacyOpen,
              closeLabel: t.close,
              content: (
                <>
                  {thumbOverflow.map(navRow)}
                  {/* ⚠️ A SUB-VIEW OF HOME, NOT A ROUTE (ADR-051) — so a hash
                      with `?view=`, which `ParentConsole` follows. */}
                  {privacyRow}
                  <div aria-hidden="true" className="mx-2.5 my-1.5 border-t border-line" />
                  <NavItem
                    icon={<SignOutIcon size={18} />}
                    tint="var(--color-danger)"
                    onClick={actions.signOut}
                  >
                    {t.signOut}
                  </NavItem>
                </>
              ),
            }
          : undefined
      }
      sidebarFooter={
        <>
          {/*
            ✅ THE WAY TO A PERSON, AT THE FOOT OF THE ONE TIER THAT HAS NO
            SUPPORT ROUTE.

            🔴 A PARENT WHOSE CHILD'S REGISTER LOOKS WRONG HAS NOWHERE TO GO. The
            attendance screen's own copy tells them a day with no register is "an
            ordinary thing and the school can be asked about it" — and the product
            offered no way to ask. Every other tier has a colleague down the
            corridor; this one is a phone in a kitchen.

            ⚠️ IT OPENS `parent-messages`, WHICH IS A REAL THREAD WITH A REAL
            TEACHER, and it is drawn only when that route exists for this role. A
            block promising help that opened a form nobody reads would be the
            dead-control failure this shell's notes are full of.

            ⚠️ AND IT PROMISES THE SCHOOL, NOT US. `MessageThread` is a parent and
            their child's teacher; Kalqy is not in it. The copy says so, because
            "Need help?" over a channel to a class teacher would send billing
            questions to somebody who cannot answer them.

            ⚠️ STOOD DOWN ON A WINDOW UNDER 32rem TALL — a phone held sideways,
            which gets this sidebar at 844×390. The card and sign-out are
            `shrink-0`, so they took the height and left the nav to scroll with
            "Achievements" cut in half under them. Messages is in the nav anyway.
          */}
          {role === "PARENT" ? (
            <a
              href={pathFor("parent-messages")}
              onClick={(e) => {
                e.preventDefault();
                navigate("parent-messages");
              }}
              className="mb-2 flex items-center gap-2.5 rounded-2xl border border-line bg-surface px-3 py-2.5 shadow-2xs transition-colors hover:border-primary/40 hover:bg-canvas [@media(max-height:32rem)]:hidden"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-tint text-primary">
                <MessageIcon size={16} />
              </span>
              <span className="min-w-0">
                <span className="block font-dash text-dash-xs font-extrabold text-ink">
                  {t.helpTitle}
                </span>
                <span className="block truncate font-dash text-[11px] font-semibold text-muted">
                  {t.helpHint}
                </span>
              </span>
            </a>
          ) : null}
          {/*
            🔴 THE DOORWAY TO A TIER'S SETTINGS, AND IT IS CHROME RATHER THAN A
            NAV ITEM. G1–G4 carry no `roles`, so `navFor` never returns them —
            deliberately, because every adult has the same four and a sidebar
            slot each would push the screens a teacher opens during a
            thirty-minute slot further down.

            ✅ AND IT NO LONGER DRAWS FOR A PARENT (ADR-051). `settingsHomeFor`
            answers `null` for that tier now, so this whole control is absent
            rather than pointing somewhere: the parent's preferences moved onto
            Privacy, which is already a nav item, and a second word for the same
            page is what this tier had before and was asked to stop having.

            ⚠️ ONE GLYPH ON EVERY TIER, INCLUDING THE PARENT'S. This row is the
            same control everywhere; giving a tier its own glyph would make the
            one shared piece of chrome the one that looks different per tier. It
            used to read that glyph off G1's nav entry — `SETTINGS_CONTROL` is
            where it lives now that G1 is gone.

            🔴 ABSENT ENTIRELY WHEN THE TIER HAS NO SETTINGS SCREEN. A principal
            is that tier — see `settingsHomeFor`. Drawing a disabled or dead
            control instead would tell them the product has a screen they are
            being denied, which is the exact thing `navFor` is written to avoid.
          */}
          {settingsRoute ? (
            <NavItem
              href={pathFor(settingsRoute)}
              icon={SETTINGS_CONTROL.icon}
              tint={SETTINGS_CONTROL.tint}
              active={inSettings}
              onClick={() => {
                navigate(settingsRoute);
              }}
            >
              {t.settings}
            </NavItem>
          ) : null}
          {/*
            ⚠️ SIGN-OUT IS IN THE SHELL, so it is on every signed-in screen. The
            device this runs on is a shared classroom laptop; a sign-out an adult
            has to hunt for is a session left open for the next class.
          */}
          <NavItem
            icon={<SignOutIcon size={18} />}
            tint="var(--color-danger)"
            onClick={actions.signOut}
          >
            {t.signOut}
          </NavItem>
        </>
      }
      /*
       * 🔴 X4 — NEVER A SILENT VIEW-AS. `admin-panel.md` §8: *"an operator who
       * forgets they are impersonating makes changes they attribute to the
       * customer."* It sits in the shell rather than on a screen because it has
       * to be true of EVERY screen the impersonated session can reach — one
       * screen that forgot to render it is the screen the mistake happens on.
       *
       * ✅ AND IT IS A FULL-WIDTH STRIP NOW, NOT A CHIP AMONG THE HEADER'S
       * LINKS. §8 requires the session be *"visibly indicated"*; a pill sitting
       * between "Settings" and "Sign out" is where the eye goes last, which is
       * technically visible and practically not.
       */
      banner={
        session?.impersonatorId ? (
          <div
            role="status"
            className="flex flex-wrap items-center justify-between gap-3 px-8 py-2.5 font-dash text-dash-sm font-bold"
            style={{
              background: "var(--color-pill-warning-bg)",
              color: "var(--color-pill-warning-fg)",
              borderBottom: "1px solid var(--color-pill-warning-line)",
            }}
          >
            <span className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-flex shrink-0">
                <PrivacyIcon size={16} />
              </span>
              {/* P0-10 — the session is read-only now (the server refuses every
                  write with IMPERSONATION_READ_ONLY), and the banner says so. */}
              {IMPERSONATION_BANNER}
            </span>
            {/*
              ✅ THE WAY OUT, WHICH THE BANNER USED NOT TO HAVE. §8 requires the
              session be *"time-boxed and visibly indicated"* — it was visible,
              and the only way to leave it was to sign out entirely and sign back
              in as the operator. So the control that made the feature safe to
              use was the one missing.
            */}
            <button
              type="button"
              onClick={actions.stopActingAs}
              className="rounded-pill border border-current px-3 py-1 font-dash text-dash-xs font-bold"
            >
              {t.impersonationEnd}
            </button>
          </div>
        ) : null
      }
      actions={
        /*
         * ⚠️ THE HEADER KEEPS THE SAME TWO CONTROLS AS THE FOOTER, AND THAT
         * DUPLICATION IS DELIBERATE FOR THE VARIANTS THAT MAY HAVE NO SIDEBAR.
         * `nav="flat"` never draws one, and `nav="adaptive"` — the parent tier —
         * draws none below `md`, so a footer-only sign-out would be a sign-out
         * that exists on a laptop and not on the phone this tier is mostly read
         * on. They are not shown twice at once: `DashboardShell` stands the
         * header strip down at exactly the width the sidebar footer appears.
         */
        nav === "sidebar" ? null : (
          <>
            {settingsRoute ? (
              <a
                href={pathFor(settingsRoute)}
                /* `aria-current` because this strip IS the navigation below `md`,
                   where there is no sidebar footer to carry the current state. */
                aria-current={inSettings ? "page" : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(settingsRoute);
                }}
                className={CHROME_BUTTON}
              >
                {t.settings}
              </a>
            ) : null}
            <button
              type="button"
              onClick={actions.signOut}
              className={CHROME_BUTTON}
            >
              {t.signOut}
            </button>
          </>
        )
      }
    >
      {/*
        🔴 THE RELEASE-NOTICE STRIP IS GONE AND A BROADCAST STRIP STANDS IN ITS
        PLACE, WHICH IS CHANGE 19 §5 AND §4 IN ONE LINE. §5: *"version updates
        are internal; no manual version communication to schools"* — the old
        strip existed to tell a teacher, a head or a parent that a version had
        shipped, so the feature it implemented is the one that was asked for
        back. §4: an operator writing to Parents, Teachers, School Admins or a
        named set of schools about anything at all is a thing the product did not
        have, and this is where it lands.

        ⚠️ ABOVE `children` AND BELOW `banner`, WHICH IS AN ORDERING DECISION.
        The impersonation banner is a warning about the session itself — the one
        thing that must never be pushed down the page by anything — and a
        broadcast is not competing with it.

        ⚠️ IT RENDERS NOTHING WHEN THERE IS NOTHING, AND FAILS SILENTLY. See
        `PlatformBroadcasts`: an error state here would put "couldn't load" above
        a teacher's class list, which is worse than showing nothing at all.
      */}
      <PlatformBroadcasts
        token={session?.token}
        dismissLabel={t.noticeDismiss}
      />
      {children}
    </DashboardShell>
  );
}

function Placeholder({ title, note }: { title: string; note: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center p-8">
      <div className="max-w-md text-center">
        <h1 className="font-dash text-dash-xl font-extrabold text-ink">
          {title}
        </h1>
        <p className="mt-2 text-dash-md text-ink-soft">{note}</p>
      </div>
    </main>
  );
}

/** What a console page shows while its chunk downloads — the page's shape, no spinner. */
function ConsoleScreenLoading() {
  return (
    <div aria-busy="true" aria-label="Loading" className="console font-dash">
      <div className="mb-2 h-7 w-56 animate-pulse rounded-md bg-surface-muted" />
      <div className="mb-6 h-4 w-80 max-w-full animate-pulse rounded-md bg-surface-muted" />
      <div className="h-64 w-full animate-pulse rounded-[14px] bg-surface-muted" />
    </div>
  );
}
